// @safetyhub/report-composer — 렌더 엔진 본체
//
// ReportTemplate(노드 트리)을 React 트리로 해석한다.
// 레이아웃은 flow(상대좌표). CSS flexbox로 구현 — 부모 기본 column, 콘텐츠만큼 auto-grow.
// 바인딩 값은 "이미 resolve된" RenderData 맵에서 bindingKey로 읽는다(Phase 1).

import type { CSSProperties, ReactNode } from 'react';
import type {
  Binding,
  BindingFormat,
  ComposerNode,
  GroupNode,
  StackNode,
  ReportTemplate,
  TableNode,
  TableCell,
  TableRepeatRegion,
  CellAggregate,
  CellCompute,
} from '@safetyhub/shared';
// 조건 평가 화이트리스트는 shared 단일 소스(evaluateCondition.ts)로 이동 — 렌더(rc)·admin 빌더·서버
// 제출 게이트가 동일 판정을 쓰게 한다. 하위호환: 아래에서 동일 심볼을 rc에서 re-export(기존 임포터 무변경).
import {
  evaluateCondition,
  CONDITION_CLAUSE_RE,
  CONDITION_IDENT_RE,
  OVERLAY_SHAPES_CONTENT_KEY,
  OVERLAY_STROKES_CONTENT_KEY,
  SLOT_SIGNATURES_CONTENT_KEY,
  overlayAxisDefaultVisible,
  resolveOverlayAxisVisible,
  readBindingValue,
} from '@safetyhub/shared';
import {
  SignatureSlotView,
  resolveSlotSignatureDisplay,
  rowSignerName,
} from './SignatureSlotView';
import type { RenderData, RenderContext } from './types';
import {
  getByPath,
  applyFormat,
  isFlagTruthy,
  resolveCellBackgroundColor,
} from './resolver';
// 배지(rangeMap display:'badge')와 병기(annotationKey)는 본문·작성폼 공용 모듈이 단일 정본 —
// 여기서 다시 그리지 말 것.
import { rangeDisplayNode } from './rangeBadge';
// 중첩 반복 조각 형상은 이 모듈이 단일 정본 — 측정(pageMeasure)도 같은 함수를 쓴다(둘이 갈리면
// 계획이 틀린 높이로 자른다).
import {
  findSoleNestedBoundary,
  pruneToChunkWindow,
  pruneToNestedBoundary,
  unitFlowChunks,
  unitHasBreakDecl,
} from './nestedRepeatBoundary';
import { resolveActiveBranchSlot } from './branchSlot';
import { effectiveGroupByCols } from './groupCols';
import { pagePx } from './page';
import { evalSafeExpr } from './safeExpr';
import {
  nodeLayoutToStyle,
  outerBoxStyle,
  innerBoxStyle,
  applyRectDecoration,
  resolveCellBorder,
  alignToFlex,
  alignToGrid,
  vAlignToTableCss,
  contentHToTextAlign,
  textStyleToCss,
  cellStyleToCss,
  type ParentContext,
} from './layout';
import { cellDiagonalOverlay } from './tableCellDiagonal';
import {
  RectPrim,
  TextPrim,
  ImagePrim,
  LinePrim,
  SpacerPrim,
} from './primitives';
import { SpecialPrim } from './special';
import {
  bindingPlaceholderText,
  bindingLabelPlaceholderText,
  bindingPlaceholderLabel,
  bindingPlaceholderColor,
  bindingPlaceholderTitle,
  computePlaceholderText,
  aggregatePlaceholderText,
  bindingProvenanceMark,
  isAuthoringUnresolvable,
  repeatEmptyHintText,
  BINDING_PLACEHOLDER_COLOR,
  BINDING_PLACEHOLDER_UNRESOLVABLE_COLOR,
  BINDING_PLACEHOLDER_UNRESOLVABLE_TITLE,
} from './placeholder';
import { FitPlaceholder } from './FitPlaceholder';
import { isAnnotatedImageValue } from './AnnotatedImageView';
import { ImageWithFallback } from './ImageWithFallback';
// 기간(date-range) 값은 객체라 셀 텍스트 경로에서 [object Object]로 새지 않게 먼저 갈라 접는다.
import { dateRangeRawText, isDateRangeValue } from './dateValue';
import { effectiveCellSourceKind } from './tableCell';

// 페이지 폭은 page(size+orientation)에서 pagePx로 산출한다(A4 세로=794px, landscape면 297mm 폭).
// page.margins는 컨테이너 padding으로 적용.

// readBindingValue(flat own-property 우선 → 점 경로 getByPath 폴백)는 `@safetyhub/shared`
// (readBindingValue.ts, 순수 모듈)로 이관(2026-08-24) — 렌더와 required 검증(rc
// requiredValidation·서버 composed-required-validation.util)이 같은 값 읽기를 쓰기 위함
// (flat 단독 검증이 점 경로 bindingKey의 중첩 값을 미입력 오판한 결함의 수정).
// 기존 import 경로(`'./RenderTemplate'`) 유지를 위한 재-export(evaluateCondition과 같은 규약).
export { readBindingValue };

/**
 * binding 1개의 값을 스코프에서 읽는다 — `bindingKey` 우선, **키가 아예 없을 때만**
 * `fallbackKey`(하위호환 키)로 재시도한다.
 *
 * 빈 배열·빈 문자열은 폴백하지 않는다: 새 형태를 쓰는 저장본은 그 키를 늘 갖고 있으므로 "비었다"는
 * 작성자가 지운 결과다(여기서 폴백하면 전부 지운 값이 옛 값으로 되살아난다). 규약 정본은
 * shared `Binding.fallbackKey` doc.
 */
export function readBindingRaw(
  scope: RenderData,
  binding: Pick<Binding, 'bindingKey' | 'fallbackKey'>,
): unknown {
  const primary = readBindingValue(scope, binding.bindingKey);
  if (primary !== undefined) return primary;
  if (!binding.fallbackKey) return primary;
  return readBindingValue(scope, binding.fallbackKey);
}

/**
 * 노드의 resolve된 바인딩 값을 RenderData에서 읽는다.
 * binding이 없으면 undefined (정적 노드).
 *
 * bindingKey가 dotted-path('a.b')이면 getByPath로 중첩 lookup, 단일 키('a')이면
 * data['a'] 단일 lookup과 동일하게 동작한다(단일 키 동작 보존).
 */
export function resolveValue(node: ComposerNode, data: RenderData): unknown {
  return node.binding ? readBindingRaw(data, node.binding) : undefined;
}

/** 예약 키 접두 — 서버·호스트 소유 키 관례(`__slotSignatures`·`__catalogParams`·`__fileHost`). */
const RESERVED_SCOPE_KEY_PREFIX = '__';

/**
 * repeat 항목 scope 머지.
 * item이 객체면 부모 data 위에 spread 머지(항목 필드가 부모를 덮음).
 * 비객체(원시값/null)면 부모 data 그대로 — 항목 값은 template 내 binding으로 못 읽으나
 * 부모 컨텍스트는 유지된다.
 *
 * ⚠️ **예약 키(`__` 접두)는 항목이 부모를 덮을 수 없다.** 부모(RenderData)에 실린 예약 키는
 * 서버 resolve 산출·호스트 주입값(`__slotSignatures` 서명 맵, `__catalogParams`, `__fileHost`)이라
 * 행 데이터(content — 작성자·위임 토큰 보유자가 편집 가능)가 같은 키를 들고 있으면 그 행에서
 * 정본이 가려진다(2026-08-30 리뷰 C1 — 행 객체에 `__slotSignatures`를 심어 서명 위조).
 * 부모에 있으면 부모 값으로 복원하고, 부모에 없으면 항목의 예약 키는 scope에서 뺀다
 * (행이 예약 키를 *새로* 들여오는 경로도 막는다 — 서버 맵이 없을 때 위조 맵이 그대로 읽히는 것).
 */
export function mergeRepeatScope(parent: RenderData, item: unknown): RenderData {
  if (item !== null && typeof item === 'object' && !Array.isArray(item)) {
    const row = item as Record<string, unknown>;
    const scope: RenderData = { ...parent, ...row };
    for (const key of Object.keys(row)) {
      if (!key.startsWith(RESERVED_SCOPE_KEY_PREFIX)) continue;
      if (Object.prototype.hasOwnProperty.call(parent, key)) scope[key] = parent[key];
      else delete scope[key];
    }
    return scope;
  }
  return parent;
}

// evaluateCondition / CONDITION_CLAUSE_RE / CONDITION_IDENT_RE 는 `@safetyhub/shared`(순수 모듈)로
// 이관했다(위 import). 렌더 엔진(rc)·admin 조건 빌더·서버 제출 게이트가 동일 판정을 쓰게 하기 위함.
// 하위호환: 기존 rc 임포터(`from './RenderTemplate'` / `from '@safetyhub/report-composer'`)가
// 무변경 유지되도록 동일 심볼을 여기서 re-export한다. RenderData(=Record<string,unknown>) 호환이므로
// 슬롯 condition 평가(evaluateCondition(slot.condition, data)) 시그니처는 그대로다.
export { evaluateCondition, CONDITION_CLAUSE_RE, CONDITION_IDENT_RE };

/**
 * 편집 캔버스(ctx.editing) 전용 — 자식 없는 group/stack의 존재를 알아볼 수 있게
 * inner 영역에 점선 테두리 + 최소 높이 + 중앙 힌트 라벨을 그린다.
 * 비편집(미리보기/상세/print/PDF)에서는 호출하지 않아 렌더 경로 무변경(회귀 0).
 */
const EMPTY_CONTAINER_HINT_LABEL: Record<'group' | 'stack' | 'branch', string> = {
  group: '빈 그룹',
  stack: '빈 스택',
  // 톤은 group/stack("빈 …")과 맞추되, 활성 슬롯이 비었음을 알리는 문구.
  branch: '빈 분기 슬롯',
};

function renderEmptyContainerHint(
  kind: 'group' | 'stack' | 'branch',
  layoutMinHeight?: number,
  asSpacer?: boolean,
  labelOverride?: string,
): ReactNode {
  const hintStyle: CSSProperties = {
    // 노드 outer 박스는 layout.minHeight(예: stack 기본 60)로 늘어나는데 힌트가 40 고정이면
    // 선택 outline과 점선이 어긋난다 → 노드 minHeight를 따라가되 최소 40 보장.
    minHeight: Math.max(40, layoutMinHeight ?? 0),
    // group=flex 자식, stack=grid 셀 — 양쪽 모두 inner 전폭으로 늘어나게.
    alignSelf: 'stretch',
    justifySelf: 'stretch',
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    // asSpacer(페이지 채움 적용 중): 시각 표현은 outer 오버레이가 담당 — 여기는 in-flow
    // 자연높이 스페이서만(테두리 투명, 라벨 미표시). inner가 이제 확장 outer에 stretch되지만,
    // measure pass가 outer를 일시-collapse(height:auto)하면 inner height:100%가 미해석되어
    // 이 스페이서의 minHeight로 collapse된다 → 자연높이 probe 의미가 그대로 성립.
    border: asSpacer ? '1px dashed transparent' : '1px dashed #cbd5e1',
    borderRadius: 4,
    color: '#94a3b8',
    fontSize: 11,
    userSelect: 'none',
  };
  return (
    <div style={hintStyle}>
      {asSpacer ? null : labelOverride ?? EMPTY_CONTAINER_HINT_LABEL[kind]}
    </div>
  );
}

/**
 * 페이지 채움(fillHeight 적용) 중인 빈 컨테이너의 점선+라벨 — outer 기준 absolute 오버레이.
 * inner(자연높이 probe)에 그리면 채움 전 크기로만 보이므로, 확장된 outer 전체(inset 0)를 덮는다.
 * absolute라 높이에 미기여 → 측정 불변식(제1원칙) 안전. pointer-events 없음(클릭은 outer가 처리).
 */
function renderEmptyContainerFillOverlay(kind: 'group' | 'stack'): ReactNode {
  const overlayStyle: CSSProperties = {
    position: 'absolute',
    inset: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    border: '1px dashed #cbd5e1',
    borderRadius: 4,
    color: '#94a3b8',
    fontSize: 11,
    userSelect: 'none',
    pointerEvents: 'none',
  };
  return (
    <div style={overlayStyle}>{kind === 'group' ? '빈 그룹' : '빈 스택'}</div>
  );
}

/**
 * 노드 1개를 재귀 렌더.
 * group → flex container, stack → grid 단일셀 z-겹침, repeat → 배열 전개,
 * special → 주입 컴포넌트(없으면 placeholder), 그 외 → 프리미티브.
 *
 * `parent`는 부모 컨테이너 컨텍스트 — size='fill'/정렬(alignH/V)을 컨텍스트별로 변환한다(undefined=flow).
 */
/**
 * 컨테이너(group/stack) 공통 페이지 채움/빈 힌트 계산.
 * 계획이 계산한 fillHeight를 outer.height에 강제하고, 빈 컨테이너 힌트 표시 여부와
 * 오버레이 배치 여부를 판정한다. group/stack case의 동일 계산 블록을 단일화한 것으로
 * 동작은 불변(부수효과로 outer를 변형).
 */
function applyPageFillToBoxes(
  node: GroupNode | StackNode,
  ctx: RenderContext,
  outer: CSSProperties,
): { isPageFill: boolean; showEmptyHint: boolean; hintOnOverlay: boolean } {
  // pageFill 단일흡수 또는 fillSplitGroup 세로분배 group/stack 멤버 모두 계획이 준 fillHeight로 outer를
  // 확장한다(표는 fillSlacks 경로가 담당). isPageFill=true면 data-composer-fill-natural도 emit해 측정
  // (pageMeasure)이 확장분을 되먹지 않게 한다(진동 불변식) — pageMeasure needsNaturalProbe와 대칭.
  const isPageFill = node.layout?.pageFill === true || node.layout?.fillSplitGroup != null;
  const fh = isPageFill ? ctx.fillHeights?.[node.id] : undefined;
  if (fh !== undefined) outer.height = fh;
  const showEmptyHint = node.children.length === 0 && !!ctx.editing;
  const hintOnOverlay = showEmptyHint && fh !== undefined;
  if (hintOnOverlay) outer.position = 'relative';
  return { isPageFill, showEmptyHint, hintOnOverlay };
}

/**
 * 컨테이너 높이가 minHeight로만 표현될 때 inner의 stretch를 복원한다.
 *
 * outer는 고정 height·채움(fill)이 아니면(=auto) nodeLayoutToStyle이 layout.minHeight를
 * outer.minHeight로 낸다(height 속성 없음). 이때 innerBoxStyle의 height:'100%'는 CSS상 부모의
 * height *속성*이 있어야 해석되므로, height 없이 minHeight만 가진 outer 안에서는 미해석 →
 * inner가 콘텐츠 높이로 붕괴한다(실측: outer 125px, inner 24px). 그 결과:
 *  - 자식 수직 정렬(contentAlignV → justify-content)이 방출돼도 움직일 공간이 없어 무반응
 *  - 자식 height 채움(flex-grow/stretch)도 붕괴한 inner 안에서 무의미
 * → inner에도 같은 minHeight를 전파해 inner가 outer 사용 높이로 stretch되게 한다.
 *
 * outer는 padding이 없고(inner로 이전) 장식(border)도 inner가 먹으므로 outer.minHeight는 곧
 * 박스 전체 최소높이 = inner(border-box)의 최소높이와 일치한다 → 값 보정 없이 그대로 복사(이중계산 없음).
 *
 * 적용 조건은 `outer.height === undefined && outer.minHeight !== undefined`로 한정한다:
 *  - pageFill 노드는 applyPageFillToBoxes가 outer.height=fh를 이미 설정 → skip(측정 pass의
 *    자연높이 probe/collapse 동작 불변, 진동 불변식 유지).
 *  - 고정 height·채움(fill)은 outer.height가 정의됨 → skip(inner height:100%가 정상 해석).
 * 따라서 반드시 applyPageFillToBoxes 이후에 호출한다.
 */
function propagateMinHeightToInner(outer: CSSProperties, inner: CSSProperties): void {
  if (outer.height === undefined && outer.minHeight !== undefined) {
    inner.minHeight = outer.minHeight;
  }
}

/**
 * 체크박스 비주얼(테두리 박스 + 체크 SVG polyline) — CheckboxNode 렌더와 bool box format(표 셀/비표
 * 노드)이 공유하는 단일 마크업. 폰트 글리프 대신 SVG polyline이라 PDF screenshot에 안전하고,
 * stroke는 currentColor를 상속한다. boxSize는 박스 한 변 길이(px).
 */
function CheckBox({
  checked,
  boxSize,
  shape = 'square',
  borderColor = '#000',
  borderWidth = 1,
  checkedColor = '#000',
}: {
  checked: boolean;
  boxSize: number;
  shape?: 'square' | 'circle';
  borderColor?: string;
  borderWidth?: number;
  checkedColor?: string;
}): ReactNode {
  const boxStyle: CSSProperties = {
    boxSizing: 'border-box',
    width: `${boxSize}px`,
    height: `${boxSize}px`,
    border: `${borderWidth}px solid ${borderColor}`,
    flexShrink: 0,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    ...(shape === 'circle' ? { borderRadius: '50%' } : null),
  };
  return (
    <span style={boxStyle}>
      {checked &&
        (shape === 'circle' ? (
          // 라디오: 중앙 채움 점(지름 ≈ boxSize*0.5)
          <span
            style={{
              width: `${Math.max(2, boxSize * 0.5)}px`,
              height: `${Math.max(2, boxSize * 0.5)}px`,
              borderRadius: '50%',
              backgroundColor: checkedColor,
              display: 'block',
            }}
            aria-hidden="true"
          />
        ) : (
          <svg
            viewBox="0 0 16 16"
            width="100%"
            height="100%"
            style={{ display: 'block' }}
            aria-hidden="true"
          >
            <polyline
              points="3.5,8.5 6.5,11.5 12.5,4.5"
              fill="none"
              stroke={checkedColor}
              strokeWidth={2}
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        ))}
    </span>
  );
}

/**
 * bool box 순번/포맷 공용 — 순번 prefix 문자열 생성(항목 인덱스 0-based → 표시 순번).
 * number: idx+1 / alphaLower: a,b,…,z,aa(엑셀식 26진, 소문자) / alphaUpper: 대문자. separator 기본 '. '.
 */
function excelAlpha(idx: number): string {
  // 0→a, 25→z, 26→aa (bijective base-26)
  let n = idx + 1;
  let s = '';
  while (n > 0) {
    const rem = (n - 1) % 26;
    s = String.fromCharCode(97 + rem) + s;
    n = Math.floor((n - 1) / 26);
  }
  return s;
}

function formatIndexPrefix(
  idx: number,
  style: 'number' | 'alphaLower' | 'alphaUpper',
  separator?: string,
): string {
  const sep = separator ?? '. ';
  if (style === 'number') return `${idx + 1}${sep}`;
  const letters = excelAlpha(idx);
  return `${style === 'alphaUpper' ? letters.toUpperCase() : letters}${sep}`;
}

export function renderNode(
  node: ComposerNode,
  data: RenderData,
  ctx: RenderContext = {},
  parent?: ParentContext,
): ReactNode {
  // 조건부 표시는 branch 노드로 일원화됨 — 노드별 condition은 폐지되었다(evaluateCondition은
  // branch 슬롯 조건 평가에서만 사용). 여기서는 kind별 렌더만 수행한다.
  switch (node.kind) {
    case 'group': {
      // 슬롯/콘텐츠 2겹 모델 — outer(크기/위치, 패딩 없음) + inner(display/contentAlign/padding/장식).
      // 패딩이 outer flex 분배에 안 끼어들어 채움 형제가 패딩 무관 균등 분할(Flutter Expanded+Padding 동형).
      const direction = node.direction ?? 'column';
      const outer = outerBoxStyle(node.layout, parent);
      const inner: CSSProperties = {
        ...innerBoxStyle(node.layout),
        display: 'flex',
        flexDirection: direction,
      };
      if (node.gap !== undefined) inner.gap = `${node.gap}px`;
      // Inner 자식 정렬(contentAlign*) — 주축/교차축으로 매핑.
      {
        const isRow = direction === 'row';
        const cH = node.layout?.contentAlignH;
        const cV = node.layout?.contentAlignV;
        const crossContent = isRow ? cV : cH; // 교차축 콘텐츠 정렬
        const mainContent = isRow ? cH : cV; // 주축 콘텐츠 정렬
        inner.alignItems = alignToFlex(crossContent ?? 'start');
        if (mainContent) inner.justifyContent = alignToFlex(mainContent);
      }
      // 박스 장식(채움색/테두리/모서리). 미설정 시 미부여(기존 동작 — 회귀 0).
      applyRectDecoration(inner, node.style);
      const childParent: ParentContext = { kind: 'group', direction };
      // 페이지 채움: 계획이 계산한 높이(fillHeights)를 outer에 강제. inner는 innerBoxStyle의
      // height:'100%'를 유지해 확장된 outer를 꽉 채운다 → 자식 fill·장식(applyRectDecoration)이
      // 확장 영역 전체를 덮는다. 측정(measure) pass는 outer를 일시-collapse(height:auto)해
      // 자연높이 probe(data-composer-fill-natural)를 읽으므로, 렌더 시 inner를 stretch해도
      // 진동 불변식(제1원칙)이 유지된다 — EditorPage.PagedEditBody.measure() 참조.
      // 빈 컨테이너 힌트: 채움(fillHeight) 적용 중이면 점선+라벨을 outer 오버레이로(확장 영역 전체),
      // inner 쪽은 투명 스페이서(자연높이 probe 유지). 미적용이면 기존처럼 inner에 점선+라벨.
      const { isPageFill, showEmptyHint, hintOnOverlay } = applyPageFillToBoxes(node, ctx, outer);
      // minHeight로만 높이가 표현된 컨테이너의 inner stretch 복원(pageFill/고정/채움은 skip).
      propagateMinHeightToInner(outer, inner);
      return (
        <div key={node.id} data-composer-node-id={node.id} style={outer}>
          <div
            style={inner}
            {...(isPageFill ? { 'data-composer-fill-natural': node.id } : {})}
          >
            {showEmptyHint
              ? renderEmptyContainerHint('group', node.layout?.minHeight, hintOnOverlay)
              : node.children.map((child) => renderNode(child, data, ctx, childParent))}
          </div>
          {hintOnOverlay && renderEmptyContainerFillOverlay('group')}
        </div>
      );
    }

    case 'stack': {
      // z-레이어 겹침 컨테이너 — CSS grid 단일 셀(1fr×1fr)에 모든 자식을 겹친다.
      // 칸 크기는 가장 큰 자식에 맞춰지고(채움 자식은 셀 전체로 stretch),
      // 각 자식 위치는 layout.alignH/alignV(justify/align-self), 채움은 width/height='fill'(stretch).
      // 슬롯/콘텐츠 2겹 모델 — outer(크기/위치, 패딩 없음) + inner(grid/padding).
      const outer = outerBoxStyle(node.layout, parent);
      const inner: CSSProperties = {
        ...innerBoxStyle(node.layout),
        display: 'grid',
        gridTemplateColumns: '1fr',
        gridTemplateRows: '1fr',
      };
      // 박스 장식(채움색/테두리/모서리). 미설정 시 미부여(기존 동작 — 회귀 0). group과 동일하게 inner에 적용.
      applyRectDecoration(inner, node.style);
      const childParent: ParentContext = { kind: 'stack' };
      // 페이지 채움: 계획이 계산한 높이(fillHeights)를 outer에 강제. inner는 height:'100%'를
      // 유지해 확장된 outer를 채운다 → grid 셀의 세로 fill 자식이 확장 영역 전체로 stretch.
      // 측정 pass는 outer 일시-collapse로 자연높이를 읽어 진동 불변식 유지(group case와 동일).
      // 빈 컨테이너 힌트: 채움 적용 중이면 outer 오버레이(group case와 동일 규칙).
      const {
        isPageFill: isPageFillStack,
        showEmptyHint: showEmptyHintStack,
        hintOnOverlay: hintOnOverlayStack,
      } = applyPageFillToBoxes(node, ctx, outer);
      // minHeight로만 높이가 표현된 컨테이너의 inner(grid) stretch 복원(pageFill/고정/채움은 skip).
      propagateMinHeightToInner(outer, inner);
      return (
        <div key={node.id} data-composer-node-id={node.id} style={outer}>
          <div
            style={inner}
            {...(isPageFillStack ? { 'data-composer-fill-natural': node.id } : {})}
          >
            {showEmptyHintStack
              ? renderEmptyContainerHint('stack', node.layout?.minHeight, hintOnOverlayStack)
              : node.children.map((child) => {
              const cl = child.layout;
              // 가로(width) 채움이면 stretch, 아니면 alignH(미지정=start). 세로(height)도 동일.
              const justifySelf =
                cl?.width === 'fill' || cl?.width === undefined
                  ? 'stretch'
                  : alignToGrid(cl?.alignH ?? 'start');
              const alignSelf =
                cl?.height === 'fill' ? 'stretch' : alignToGrid(cl?.alignV ?? 'start');
              const cellStyle: CSSProperties = {
                gridColumn: '1',
                gridRow: '1',
                justifySelf,
                alignSelf,
                minWidth: 0,
                minHeight: 0,
              };
              return (
                <div key={child.id} style={cellStyle}>
                  {renderNode(child, data, ctx, childParent)}
                </div>
              );
            })}
          </div>
          {hintOnOverlayStack && renderEmptyContainerFillOverlay('stack')}
        </div>
      );
    }

    case 'repeat': {
      const rawItems = (data[node.over] as unknown[] | undefined) ?? [];
      // 빈 template(기본형 직후) 편집 캔버스 — renderNode(group) 경유 시 힌트 라벨이 '빈 그룹'이라
      // 운영자가 별개 노드로 오해(삭제 시도 등)한다. template group 위치에 '빈 반복 단위' 라벨 힌트를
      // 직접 그려 구조(반복의 일부, 개별 삭제 불가)를 드러낸다. 클릭 선택은 template.id 그대로 동작.
      if (ctx.editing === true && node.template.children.length === 0) {
        return (
          <div
            key={node.id}
            data-composer-node-id={node.id}
            style={nodeLayoutToStyle(node.layout, parent)}
          >
            <div data-composer-node-id={node.template.id} style={{ width: '100%' }}>
              {renderEmptyContainerHint(
                'group',
                node.template.layout?.minHeight,
                false,
                '빈 반복 단위',
              )}
            </div>
          </div>
        );
      }
      // 'unresolvable-only'(실데이터 반영 중) + 0행 — 반복 단위가 통째로 사라져 저작자가 원인을
      // 알 수 없는 상황(자동연결 폐기로 tbm-sessions gap 실측)이라, 뼈대(유령 행) 대신 **한 줄
      // 빈 힌트**를 amber 톤으로 그린다: 「{소스 라벨} 실데이터 0행」(+행 수 모사 대상이면 안내
      // 꼬리 — 판정은 repeatEmptyHintText 정본). over가 선언 dataSource와 미매칭이면(행 스코프
      // 필드 반복 등) 힌트 없이 기존 빈 컨테이너 그대로. web은 이 모드를 안 켜므로 무영향.
      if (rawItems.length === 0 && ctx.bindingPlaceholders === 'unresolvable-only') {
        const emptyHint = repeatEmptyHintText(node.over, ctx.dataSources);
        if (emptyHint !== undefined) {
          return (
            <div
              key={node.id}
              data-composer-node-id={node.id}
              data-composer-repeat-items={0}
              style={nodeLayoutToStyle(node.layout, parent)}
            >
              <div
                style={{ color: BINDING_PLACEHOLDER_UNRESOLVABLE_COLOR, fontSize: 11 }}
                title={BINDING_PLACEHOLDER_UNRESOLVABLE_TITLE}
              >
                {emptyHint}
              </div>
            </div>
          );
        }
      }
      // 편집 캔버스(ctx.editing) + **샘플(placeholder) 모드**에서 items가 비면 [{}] 1항목으로 template을
      // 1회 렌더하는 편집 뼈대(표 반복영역 [{}] seed와 동형 — 반복 단위 구성·편집용).
      // 실데이터 반영 중(bindingPlaceholders===false)에는 뼈대 없음 — 실데이터 0행인데 유령 행이
      // 실값 옆에 섞여 출력물로 오독됨(2026-07-06 서명란 부 점검자 0명 사례). WYSIWYG.
      // 'unresolvable-only'(실데이터 반영 중의 미해소 표시 모드)도 뼈대 없음(false와 동일 — 0행
      // 상황은 위의 빈 힌트가 담당) — 반복 행 존재 여부는 실렌더를 따른다.
      // 비편집(미리보기/상세/print/PDF)은 빈 배열=미렌더(회귀 0).
      const skeletonSeed =
        rawItems.length === 0 &&
        ctx.editing === true &&
        ctx.bindingPlaceholders !== false &&
        ctx.bindingPlaceholders !== 'unresolvable-only';
      const items = skeletonSeed ? [{}] : rawItems;
      // 다단(columns≥2): 항목을 CSS grid 가로 N단으로 배치(구 SMS 이행상태 카드 2단 grid 동형).
      // gap은 다단 grid gap / 세로 나열의 행 간격 공용. 미지정=기존 세로 나열(회귀 0).
      const containerStyle: CSSProperties = { ...nodeLayoutToStyle(node.layout, parent) };
      if ((node.columns ?? 1) >= 2) {
        containerStyle.display = 'grid';
        containerStyle.gridTemplateColumns = `repeat(${node.columns}, 1fr)`;
        if (node.gap !== undefined) containerStyle.gap = `${node.gap}px`;
        // grid 셀 안 카드가 셀 폭을 넘지 않게(표 width:100% 기준 확보).
        containerStyle.alignItems = 'start';
      } else if (node.gap !== undefined) {
        containerStyle.display = 'flex';
        containerStyle.flexDirection = 'column';
        containerStyle.gap = `${node.gap}px`;
      }
      // 페이지 분할(itemRanges): 이 repeat에 범위가 지정되면 그 항목 범위만 렌더한다(표 rowRanges와
      // 대칭). key·마커 인덱스는 원본 항목 인덱스를 유지해 여러 낱장에 서로 다른 범위로 조각 렌더돼도
      // 편집 매칭이 보존된다. 미지정이면 전체 렌더(회귀 0). 뼈대([{}])는 1항목이라 분할 대상이 아니다.
      // 조회 키는 **인스턴스 경로**로 확장한다(ctx.instancePath 주석 참조) — 중첩 반복에서 안쪽
      // repeat은 노드 1개인데 인스턴스가 바깥 항목 수만큼이라 nodeId 전역 키로는 인스턴스별로 다른
      // 범위를 지시할 수 없다. 최상위(비중첩)는 instancePath가 없어 키 = node.id 그대로(회귀 0).
      const rangeKey = ctx.instancePath ? `${ctx.instancePath}/${node.id}` : node.id;
      const itemRange = ctx.itemRanges?.[rangeKey];
      const rangeStart = itemRange ? Math.max(0, itemRange[0]) : 0;
      const rangeEnd = itemRange ? Math.min(items.length, itemRange[1]) : items.length;
      const rendered: ReactNode[] = [];
      for (let i = rangeStart; i < rangeEnd; i += 1) {
        const scope = mergeRepeatScope(data, items[i]);
        // template은 GroupNode — 항목별로 원본 인덱스 key + 측정 마커를 부여해 렌더
        rendered.push(
          <RepeatItem
            key={i}
            template={node.template}
            data={scope}
            ctx={ctx}
            marker={`${node.id}::${i}`}
            instancePath={`${ctx.instancePath ? `${ctx.instancePath}/` : ''}${node.id}::${i}`}
          />,
        );
      }
      return (
        <div
          key={node.id}
          data-composer-node-id={node.id}
          // 클립 무관 진짜 총 항목수 — admin 페이지 분할 측정이 stale plan(데이터 증가로 새 항목이
          // 클립됨)을 감지하는 데 쓴다(표 data-composer-table-rows와 대칭, 무해 attribute).
          data-composer-repeat-items={items.length}
          style={containerStyle}
        >
          {rendered}
        </div>
      );
    }

    case 'branch': {
      // 조건 분기 슬롯 컨테이너 — 첫 매칭 슬롯의 children만 group column과 동일한 수직 flow로 렌더.
      // 활성 슬롯 판정은 resolveActiveBranchSlot(branchSlot.ts) 단일 소스 — 측정(pageMeasure)·낱장
      // 재조립(branchPagination)이 같은 함수를 써야 "측정한 슬롯"과 "그린 슬롯"이 갈리지 않는다.
      // (편집 캔버스 탭 선택 vs 조건 평가 분기의 근거는 그 함수 주석 참조.)
      const activeIndex = resolveActiveBranchSlot(node, data, ctx);
      if (activeIndex < 0) return null; // 매칭 슬롯 없음 → 미렌더
      const slot = node.branches[activeIndex];
      // branch는 "투명 논리 컨테이너" — 시각 요소가 아니라 조건 분기 구조일 뿐이라 자체 레이아웃
      // (크기/정렬/최소높이/채움/장식)을 갖지 않는다. outer는 layout·parent를 무시한 고정 투명
      // 스타일(가로 전폭 + 콘텐츠 높이 auto)이라, 크기는 항상 활성 슬롯 콘텐츠가 결정한다. 고정
      // 크기가 필요하면 슬롯 안 그룹에 지정한다(결재란 content-hug 전례의 확장). 기존 DB 노드에
      // layout 값(width/minHeight 등)이 있어도 무시되므로 하위 호환이 자연 확보된다. inner는 group
      // column과 동일한 수직 flow만 유지(장식/정렬/패딩 없음).
      const outer: CSSProperties = { width: '100%' };
      const inner: CSSProperties = {
        display: 'flex',
        flexDirection: 'column',
      };
      const childParent: ParentContext = { kind: 'group', direction: 'column' };
      // 편집 캔버스(ctx.editing)에서 활성 슬롯이 비었으면 group과 동일하게 점선+라벨 힌트를 그려
      // 슬롯 영역(클릭·드롭 대상)이 보이게 한다. 비편집(미리보기/상세/print/PDF)은 미표시(회귀 0).
      // branch는 pageFill 대상이 아니라 오버레이 스페이서는 불필요 — in-flow 힌트만. 힌트 최소높이는
      // layout을 무시하고 renderEmptyContainerHint의 실효 40px floor로만 가시성을 확보한다.
      const showEmptySlotHint = ctx.editing === true && slot.children.length === 0;
      // 빈 슬롯 힌트에 슬롯명을 포함 — label 있으면 그 이름, 없으면 슬롯 번호로 어느 슬롯인지 알린다.
      const slotHintLabel = slot.label?.trim()
        ? `『${slot.label.trim()}』 — 빈 분기 슬롯`
        : `빈 분기 슬롯 ${activeIndex + 1}`;
      return (
        <div key={node.id} data-composer-node-id={node.id} style={outer}>
          <div style={inner}>
            {showEmptySlotHint
              ? renderEmptyContainerHint('branch', undefined, false, slotHintLabel)
              : slot.children.map((child) => renderNode(child, data, ctx, childParent))}
          </div>
        </div>
      );
    }

    case 'special': {
      // 주입된 외부 컴포넌트 우선, 없으면 placeholder (packages→apps 역의존 회피)
      const Injected = ctx.specialComponents?.[node.special];
      if (Injected) {
        // 주입 컴포넌트는 외부(web/admin) 소유라 data-composer-node-id를 직접 제어할 수 없으므로
        // wrapper로 감싸 선택 역추적을 가능케 한다. 다른 노드(group/repeat/prim)와 동일하게
        // nodeLayoutToStyle(node.layout)을 적용한 실제 박스 wrapper를 둔다 — 이래야 SelectionStyles의
        // outline이 보인다(과거 display:'contents'는 박스가 없어 outline 미표시 버그였음).
        //
        // approvalBlock special만 예외 — 결재 표는 본질적으로 content-sized라 가로·세로 모두
        // content-hug로 둔다(결정 기록 2026-06-22-composer-approval-block-sizing-alignment B안).
        // wrapper의 width(fill→100%)·minHeight를 크기로 적용하지 않고 content 크기를 쓰며,
        // 위치는 layout.alignH(교차축 alignSelf, 기본 우측 'end')으로 결정 → 선택 박스가 실제 표에 밀착.
        const isApprovalBlock = node.special === 'approvalBlock';
        const base = nodeLayoutToStyle(node.layout, parent);
        // approvalBlock은 content-hug(fit-content). content-hug라 size=fill 스타일(flexGrow/stretch)을
        // 무력화하고, 교차축 정렬은 layout.alignH(미지정=end 기본은 createDefaultNode가 제공)로 명시한다.
        const wrapperStyle: CSSProperties = isApprovalBlock
          ? {
              ...base,
              flexGrow: 0,
              flexShrink: 0,
              flexBasis: 'auto',
              alignSelf: alignToFlex(node.layout.alignH ?? 'start'),
              width: 'fit-content',
              maxWidth: '100%',
              minHeight: undefined,
            }
          : base;
        return (
          <div
            key={node.id}
            data-composer-node-id={node.id}
            style={wrapperStyle}
          >
            <Injected
              node={node}
              data={data}
              value={applyFormat(resolveValue(node, data), node.binding?.format, data)}
            />
          </div>
        );
      }
      return <SpecialPrim key={node.id} node={node} parent={parent} />;
    }

    case 'rect':
      return <RectPrim key={node.id} node={node} value={resolveValue(node, data)} parent={parent} />;

    case 'text': {
      // resolve는 raw만 적재(resolveNodeBinding) → placeholder 게이트는 raw 기준(빈값이면 placeholder).
      // 미적용 카탈로그 바인딩이면 {label} placeholder(회색). 옵션 off/실값 있으면 undefined.
      const raw = resolveValue(node, data);
      const placeholder = bindingPlaceholderText(node.binding, raw, ctx, node.binding?.format, data);
      const fmt = node.binding?.format;
      // bool box format(비표 노드) — 미적용(placeholder) 아닌 실렌더면 문자 대신 체크박스 비주얼(표 셀과 동일 헬퍼).
      if (placeholder === undefined && fmt?.kind === 'bool' && fmt.display === 'box') {
        const boxWrap: CSSProperties = {
          ...nodeLayoutToStyle(node.layout, parent),
          display: 'inline-flex',
          alignItems: 'center',
        };
        const cH = node.layout?.contentAlignH;
        if (cH) boxWrap.justifyContent = alignToFlex(cH);
        return (
          <div key={node.id} data-composer-node-id={node.id} style={boxWrap}>
            <CheckBox checked={isFlagTruthy(raw)} boxSize={fmt.boxSize ?? 13} />
          </div>
        );
      }
      // fallback/empty가 placeholder를 이기지 않게: placeholder가 뜨면 raw(빈값)를 그대로 넘겨
      // TextPrim이 placeholder를 그리고, 아니면 format을 render에서 1회 적용한 표시값을 넘긴다
      // (비표 노드 resolve=raw / render=format 정렬 — 표 셀 renderTableCell과 동일 원칙, 이중 적용 방지).
      const value = placeholder !== undefined ? raw : applyFormat(raw, fmt, data);
      // rangeMap 표시(비표 노드) — 실렌더 + 구간 매칭일 때 표시 문자열을 알약으로 감싸고,
      // 병기 값(annotationKey)이 있으면 그 옆에 나란히 붙인다. 둘 다 없으면 undefined라
      // 종전 텍스트 그대로(회귀 0). TextPrim이 ReactNode 값을 받아 그리므로 폰트/색/정렬은
      // 텍스트일 때와 동일하다.
      const rangeNode =
        placeholder === undefined ? rangeDisplayNode(raw, fmt, data, String(value ?? '')) : undefined;
      return (
        <TextPrim
          key={node.id}
          node={node}
          value={rangeNode ?? value}
          parent={parent}
          placeholder={placeholder}
          // 'unresolvable-only' 모드의 placeholder는 amber 톤 + 안내 툴팁(그 외 undefined = 기존 회색).
          placeholderColor={placeholder !== undefined ? bindingPlaceholderColor(ctx) : undefined}
          placeholderTitle={placeholder !== undefined ? bindingPlaceholderTitle(ctx) : undefined}
          // 실값 갈래(placeholder 미표시)에서만 값 출처 밑줄 — placeholder 축과 배타(빈값↔실값).
          provenance={
            placeholder === undefined ? bindingProvenanceMark(node.binding, raw, ctx) : undefined
          }
        />
      );
    }

    case 'image': {
      // resolve raw → render에서 format(url host 치환 등) 1회 적용(비표 노드 render=format).
      // 주석 이미지 값(객체)은 format을 **안쪽 url 문자열에만** 적용한다 — 객체를 통째로 넘기면
      // url format(host 치환)이 문자열화해 shapes/strokes가 통째로 사라진다.
      const rawImg = resolveValue(node, data);
      let imgValue: unknown;
      if (isAnnotatedImageValue(rawImg)) {
        const formattedUrl = applyFormat(rawImg.url, node.binding?.format, data);
        imgValue = { ...rawImg, url: typeof formattedUrl === 'string' ? formattedUrl.trim() : '' };
      } else {
        imgValue = applyFormat(rawImg, node.binding?.format, data);
      }
      // 오버레이 표시 판정(값이 주석 객체일 때만 의미). 두 축이 **서로 독립**이다
      // (구 보고서 showHazardOverlay/showAnnotationOverlay 별도 토글 패리티):
      //  - 사각형(shapes) 축: 기본값 shapesVisible(미지정=true), 읽기 키 = key ?? 규약
      //    (OVERLAY_SHAPES_CONTENT_KEY). key는 데이터 레벨 오버라이드(에디터 UI 미노출).
      //  - 수기 선(strokes) 축: strokesVisible / strokesKey ?? OVERLAY_STROKES_CONTENT_KEY.
      // 판정은 shared 헬퍼 2단(admin 속성 패널과 동일 규칙 공유):
      //  - overlayAxisDefaultVisible: 저작 기본값(구 3값 mode는 'never'만 숨김으로 정규화).
      //  - resolveOverlayAxisVisible: 작성 토글 값이 정의돼 있으면 플래그 truthy 규약
      //    (isFlagTruthy와 동일 — 문자열 'false'도 숨김), undefined/null만 기본값.
      // **node.overlay 미지정이어도 규약 키는 항상 읽는다**(작성폼에 규약 키 체크박스를
      // 배치하는 것만으로 작성자 제어 성립). 둘 다 숨김 → { url }.
      // AnnotatedImageView는 도형 0이면 평면 이미지와 동일 마크업이라 사진 자체는 그대로다.
      if (isAnnotatedImageValue(imgValue)) {
        const ov = node.overlay;
        const axisHidden = (defaultVisible: boolean, bindingKey: string): boolean =>
          !resolveOverlayAxisVisible(readBindingValue(data, bindingKey), defaultVisible);
        const shapesHidden = axisHidden(
          overlayAxisDefaultVisible(ov?.shapesVisible, ov?.mode),
          ov?.key ?? OVERLAY_SHAPES_CONTENT_KEY,
        );
        const strokesHidden = axisHidden(
          overlayAxisDefaultVisible(ov?.strokesVisible, ov?.strokesMode),
          ov?.strokesKey ?? OVERLAY_STROKES_CONTENT_KEY,
        );
        if (shapesHidden || strokesHidden) {
          imgValue = {
            url: imgValue.url,
            ...(shapesHidden ? {} : { shapes: imgValue.shapes }),
            ...(strokesHidden ? {} : { strokes: imgValue.strokes }),
          };
        }
      }
      // 값 없음 판정: 주석 이미지는 url이 비면 "사진 없음"(도형만 있는 값은 그릴 대상이 없다).
      const imgEmpty = isAnnotatedImageValue(imgValue)
        ? imgValue.url === ''
        : imgValue === undefined || imgValue === null || imgValue === '';
      // 값 없는 이미지:
      //  - 실렌더(비편집)는 항상 미렌더(src="" <img> 깨진 아이콘 방지, 회귀 0).
      //  - 편집 캔버스 + 실데이터 반영 중(bindingPlaceholders===false)의 바인딩 이미지도 미렌더
      //    (WYSIWYG — 예: 서명 없으면 "(서명)" 텍스트만 보이는 게 정답).
      //  - 편집 캔버스 + 샘플/바인딩 보기 모드: 점선 사각형 + 이미지 아이콘 placeholder로
      //    "여기에 이미지가 바인딩됨"을 표시(텍스트 바인딩의 {라벨} placeholder와 동등한 편집 정보).
      //    라벨은 title 툴팁(작은 서명 칸에서 텍스트 넘침 방지). 바인딩 없는 미설정 이미지도 동일
      //    박스(아이콘 없이) — 안 보이면 선택·바인딩 설정이 불가.
      if (imgEmpty) {
        if (ctx.editing !== true) return null;
        const bound = node.binding !== undefined;
        if (bound && ctx.bindingPlaceholders === false) return null;
        // 'unresolvable-only': 저작 중 원리적 부재(live/보고서-스코프)인 바인딩만 박스 표시,
        // 그 외 빈 바인딩 이미지는 실렌더처럼 미렌더(false와 동일 — 진짜 빈 실데이터).
        // 바인딩 없는 미설정 이미지 박스는 모드 무관 유지(안 보이면 선택·바인딩 설정 불가).
        if (
          bound &&
          ctx.bindingPlaceholders === 'unresolvable-only' &&
          !isAuthoringUnresolvable(node.binding!, ctx.catalog)
        ) {
          return null;
        }
        const label = bound ? bindingPlaceholderLabel(node.binding!, ctx.catalog) : undefined;
        // 'unresolvable-only'에서 표시되는 박스는 amber 톤 + 안내 툴팁(테두리/아이콘 색만 —
        // 크기·두께 불변, 레이아웃 중립). 그 외 모드는 기존 회색.
        const unresolvableTitle = bound ? bindingPlaceholderTitle(ctx) : undefined;
        const tone = bound ? bindingPlaceholderColor(ctx) : BINDING_PLACEHOLDER_COLOR;
        const borderColor = tone === BINDING_PLACEHOLDER_COLOR ? '#d1d5db' : tone;
        return (
          <div
            key={node.id}
            data-composer-node-id={node.id}
            title={
              label
                ? `이미지 바인딩: ${label}${unresolvableTitle ? ` — ${unresolvableTitle}` : ''}`
                : undefined
            }
            style={{
              ...nodeLayoutToStyle(node.layout, parent),
              // 배경 투명 — 스택 겹침에서 아래 콘텐츠("(서명)" 텍스트 등)가 비쳐야 한다.
              border: `1px dashed ${borderColor}`,
              minHeight: Math.max(20, node.layout?.minHeight ?? 0),
              boxSizing: 'border-box',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            {bound && (
              // 이미지 아이콘(산 모양) — 편집 전용 placeholder라 모드 톤 색(기본 회색/미해소 amber).
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={tone} strokeWidth="1.6" aria-hidden="true">
                <rect x="3" y="4" width="18" height="16" rx="2" />
                <circle cx="9" cy="10" r="1.6" />
                <path d="M4.5 18.5 10 13l3.5 3.5L17 13l2.5 2.5" />
              </svg>
            )}
          </div>
        );
      }
      return (
        <ImagePrim
          key={node.id}
          node={node}
          value={imgValue}
          parent={parent}
        />
      );
    }

    case 'line':
      return <LinePrim key={node.id} node={node} value={resolveValue(node, data)} parent={parent} />;

    case 'table':
      return renderTable(node, data, ctx, parent);

    case 'spacer':
      return <SpacerPrim key={node.id} node={node} value={resolveValue(node, data)} parent={parent} />;

    case 'checkbox': {
      // 테두리 박스 + 체크 표시(SVG) + 라벨이 한 시각 단위(inline-flex).
      // 체크 판정: binding 있으면 raw 값을 checkedValue(라디오식)와 비교하거나 truthy 판정,
      // binding 없으면 정적 node.checked.
      const raw = resolveValue(node, data);
      const boxSize = node.boxSize ?? 13;
      // placeholder: 바인딩 미적용(빈값)이면 라벨 자리에 회색 {라벨} 표시 + 박스 미체크(실렌더 off면 undefined).
      const placeholder = bindingPlaceholderText(node.binding, raw, ctx, node.binding?.format, data);
      // checkedValue='' 는 모순(raw!=='' && String(raw)==='' 는 절대 참 불가)이라 미지정(truthy 모드)으로 정규화.
      const cv = node.checkedValue === '' ? undefined : node.checkedValue;
      let checked = false;
      if (placeholder === undefined) {
        if (node.binding) {
          checked =
            cv !== undefined
              ? raw !== null && raw !== undefined && raw !== '' && String(raw) === cv
              : isFlagTruthy(raw);
        } else {
          checked = node.checked ?? false;
        }
      }
      const vAlignMap = { top: 'flex-start', center: 'center', bottom: 'flex-end' } as const;
      const containerStyle: CSSProperties = {
        ...nodeLayoutToStyle(node.layout, parent),
        display: 'inline-flex',
        alignItems: vAlignMap[node.labelVAlign ?? 'center'],
        gap: node.labelGap != null ? `${node.labelGap}px` : '0.35em',
      };
      const labelCss = textStyleToCss(node.labelStyle);
      const labelEl =
        placeholder !== undefined ? (
          // 'unresolvable-only' 모드는 amber 톤 + 안내 툴팁(그 외 기존 회색, title 없음).
          <span
            style={{ ...labelCss, color: bindingPlaceholderColor(ctx) }}
            title={bindingPlaceholderTitle(ctx)}
          >
            {placeholder}
          </span>
        ) : node.label ? (
          <span style={labelCss}>{node.label}</span>
        ) : null;
      const boxEl = (
        <CheckBox
          checked={checked}
          boxSize={boxSize}
          shape={node.boxShape}
          borderColor={node.boxBorderColor}
          borderWidth={node.boxBorderWidth}
          checkedColor={node.checkedColor}
        />
      );
      const labelBefore = node.labelPosition === 'before';
      return (
        <div key={node.id} data-composer-node-id={node.id} style={containerStyle}>
          {labelBefore ? (
            <>
              {labelEl}
              {boxEl}
            </>
          ) : (
            <>
              {boxEl}
              {labelEl}
            </>
          )}
        </div>
      );
    }

    case 'choiceGroup': {
      // 선택지 그룹 — 목록·값·라벨을 저장하지 않고 렌더 시점에 dataDef options에서 파생.
      // over 키(셀이 속한 반복영역) → dataDefs에서 DataDef → binding.bindingKey 필드의 options.
      const raw = resolveValue(node, data);
      const overKey = ctx.cellOverKey;
      const fieldKey = node.binding?.bindingKey;
      const def = overKey !== undefined ? ctx.dataDefs?.find((d) => d.key === overKey) : undefined;
      const field = fieldKey !== undefined ? def?.fields.find((f) => f.key === fieldKey) : undefined;
      const options = field?.options;
      const boxSize = node.boxSize ?? 13;
      const vAlignMap = { top: 'flex-start', center: 'center', bottom: 'flex-end' } as const;
      const labelCss = textStyleToCss(node.labelStyle);

      // 파생 실패(repeat 밖 / over가 dataDef 아님 / 필드 없음 / options 없음):
      //  - 편집 캔버스: 회색 placeholder로 저작자에게 노출
      //  - 실렌더: 행 값을 텍스트로 표시(값 자체는 잃지 않음)
      if (!options || options.length === 0) {
        if (ctx.editing === true) {
          return (
            <div key={node.id} data-composer-node-id={node.id} style={nodeLayoutToStyle(node.layout, parent)}>
              <span style={{ ...labelCss, color: BINDING_PLACEHOLDER_COLOR }}>{'{선택지: 데이터셋 옵션 없음}'}</span>
            </div>
          );
        }
        const text = raw === null || raw === undefined ? '' : String(raw);
        return (
          <div key={node.id} data-composer-node-id={node.id} style={nodeLayoutToStyle(node.layout, parent)}>
            <span style={labelCss}>{text}</span>
          </div>
        );
      }

      const containerStyle: CSSProperties = {
        ...nodeLayoutToStyle(node.layout, parent),
        display: 'inline-flex',
        flexDirection: node.direction === 'column' ? 'column' : 'row',
        flexWrap: 'wrap',
        gap: `${node.gap ?? 8}px`,
      };
      // 체크 판정: 행 값이 빈값 아니고 option.value와 문자열 일치(작성 radio 저장값과 동일 소스).
      const isChecked = (value: string): boolean =>
        raw !== null && raw !== undefined && raw !== '' && String(raw) === value;
      return (
        <div key={node.id} data-composer-node-id={node.id} style={containerStyle}>
          {options.map((opt) => (
            <span
              key={opt.value}
              style={{
                display: 'inline-flex',
                alignItems: vAlignMap[node.labelVAlign ?? 'center'],
                gap: node.labelGap != null ? `${node.labelGap}px` : '0.35em',
              }}
            >
              <CheckBox
                checked={isChecked(opt.value)}
                boxSize={boxSize}
                shape={node.boxShape}
                borderColor={node.boxBorderColor}
                borderWidth={node.boxBorderWidth}
                checkedColor={node.checkedColor}
              />
              {opt.label ? <span style={labelCss}>{opt.label}</span> : null}
            </span>
          ))}
        </div>
      );
    }
  }
}

/**
 * repeat 항목 1개 — template(GroupNode)을 머지된 scope로 렌더.
 *
 * wrapper div에 항목별 측정 마커 `data-composer-repeat-item="{repeatNodeId}::{원본 인덱스}"`를
 * 방출한다 — admin 페이지 분할(itemRanges)이 항목 높이를 DOM에서 수집하는 anchor(표
 * data-composer-row와 대칭, 무해 attribute — web/PDF 영향 0). wrapper는 스타일 없는 block div라
 * 세로 나열(block/flex column)·다단 grid 모두에서 기존 항목 박스와 동일하게 배치된다.
 */
function RepeatItem({
  template,
  data,
  ctx,
  marker,
  instancePath,
}: {
  template: GroupNode;
  data: RenderData;
  ctx: RenderContext;
  marker: string;
  instancePath: string;
}) {
  // 항목 서브트리에 인스턴스 경로를 실어 내린다(per-item clone — 형제 항목으로 누출 없음).
  // 안쪽 repeat이 이 경로로 자기 인스턴스의 분할 범위를 조회한다(ctx.instancePath 주석 참조).
  // 마커 포맷은 그대로다 — 측정은 조상 체인(closest)으로 경로를 복원하므로 변경이 불필요하다.
  const itemCtx: RenderContext = { ...ctx, instancePath };
  // 중첩 분할(P6-1): 이 항목에 안쪽 경계(반복 항목 또는 **표 행**)가 있으면 조각 형상 규약이 걸린다.
  //  - continuation 조각(leadSuppressed): 경계로 가는 **체인만** 렌더(사진 되풀이 방지).
  //  - lead 보유 조각: `data-composer-lead` 마커를 방출한다 — 측정이 "이 조각이 고정분을 그렸다"를
  //    **DOM에서 직접** 알아야 lead 높이를 조각 수와 무관하게 한 번만 재기 때문이다(휴리스틱 금지).
  // 경계가 표면 조각의 행 범위는 `ctx.rowRanges[{항목경로}/{표id}]`로 이미 소비된다(renderTable).
  // 유일 경계 승격(2026-09-05): 청크열에 pageBreak:'before' 선언이 있는 단위는 유일 경계
  // 형상이어도 청크 경로다 — 측정(pageMeasure innerNode 게이트)과 **같은 함수**(unitHasBreakDecl)
  // 로 판정해야 계획이 낸 chunkRanges를 렌더가 소비한다(갈리면 창 지시가 무시돼 계획↔렌더 어긋남).
  const innerId = unitHasBreakDecl(template) ? null : soleNestedBoundaryId(template);
  const suppressed = innerId != null && ctx.leadSuppressed?.[instancePath] === true;
  let shape = suppressed ? (pruneToNestedBoundary(template, innerId) ?? template) : template;
  // 순차 다경계(결정 2026-09-03 심야): 유일 경계가 없는 항목에 계획이 청크 창을 지시하면 창의
  // 청크(+거기로 가는 branch 래퍼)만 렌더한다. 유일 경계 형상(innerId != null)에서는 계획이 이
  // 채널을 내지 않지만, 소비 게이트도 걸어 두 모델이 겹치지 않게 한다(회귀 0).
  const win = innerId == null ? ctx.chunkRanges?.[instancePath] : undefined;
  if (win) {
    const chunkIds = unitFlowChunkIds(template);
    shape = pruneToChunkWindow(
      template,
      new Set(chunkIds.slice(Math.max(0, win[0]), Math.min(chunkIds.length, win[1]))),
    );
  }
  return (
    <div
      data-composer-repeat-item={marker}
      // 경계가 있는 항목에만 방출(비중첩 항목은 종전과 동일한 DOM — 회귀 0).
      data-composer-lead={innerId != null && !suppressed ? instancePath : undefined}
    >
      {renderNode(shape, data, itemCtx)}
    </div>
  );
}

/**
 * 항목 template의 **유일한 분할 경계**(안쪽 repeat 또는 안쪽 표) id — 없으면 null. 렌더 경로에서
 * 항목마다 트리를 다시 걷지 않도록 template 객체 기준으로 캐시한다(template은 노드 트리라 항목 간
 * 동일 참조).
 */
const soleNestedBoundaryCache = new WeakMap<GroupNode, string | null>();
function soleNestedBoundaryId(template: GroupNode): string | null {
  const hit = soleNestedBoundaryCache.get(template);
  if (hit !== undefined) return hit;
  const found = findSoleNestedBoundary(template)?.id ?? null;
  soleNestedBoundaryCache.set(template, found);
  return found;
}

/**
 * 항목 template의 **흐름 청크 id 배열**(순차 다경계 창 조회용) — 인덱스가 계획(chunkRange)과
 * 정확히 같은 `unitFlowChunks` 순서다. 항목마다 트리를 다시 걷지 않도록 template 기준 캐시
 * (soleNestedBoundaryCache와 같은 규약).
 */
const unitFlowChunkIdsCache = new WeakMap<GroupNode, string[]>();
function unitFlowChunkIds(template: GroupNode): string[] {
  const hit = unitFlowChunkIdsCache.get(template);
  if (hit !== undefined) return hit;
  const ids = unitFlowChunks(template).map((n) => n.id);
  unitFlowChunkIdsCache.set(template, ids);
  return ids;
}

// ── Table 렌더 (HWPX 동형 좌표맵: 반복영역 전개 → 2D 그리드 배치 → HTML table) ─────
// 셀 content가 renderNode를 재귀 호출하므로 primitives가 아닌 이 파일에 둔다(순환 import 회피).
//
// 페이지 분할(pageMeasure/pagePlan — `data-composer-row` 행 측정 → `rowRanges` 클립)이 <tr> 행 단위로 동작하므로,
// 출력은 반드시 HTML table 행/셀(<table>/<tr>/<td>) 구조를 유지한다(이 호환성이 페이지네이션의 전제).

/** 전개 결과 셀: 원본 셀 + (반복으로 시프트된) 절대 row 좌표 + resolve scope */
export interface ExpandedCell {
  cell: TableCell;
  /** 전개 후 절대 행 좌표 */
  row: number;
  /** 셀 binding을 읽을 데이터 scope(반복 항목 머지 or 부모 data) */
  scope: RenderData;
  /**
   * groupByCol 병합으로 산출된 rowspan. 설정 시 렌더에서 cell.rowspan 대신 사용한다.
   * (원본 cell 객체는 복제본끼리 공유하므로 변형하지 않고 여기에 덮어쓴다.)
   */
  rowSpanOverride?: number;
  /**
   * 페이지 분할(rowRanges) continuation 셀 표식 — 원본 시작행이 rangeStart보다 앞선 rowspan 셀을
   * rangeStart 위치에 다시 방출한 재방출본. groupContinuationMark ON + groupByCol 칼럼이면 라벨에
   * ` (계속)` 접미어를 붙이는 판단에 쓴다(원본 셀 방출본은 false).
   */
  isContinuation?: boolean;
  /**
   * 편집 매칭용 *정체성* 열 좌표(원본 패턴 col). 다단(모델 A) 우측 단(c>0) 복제 셀은 배치용 col은
   * 시프트된 colC이지만, `data-composer-cell`엔 이 값(원본 패턴 col)을 방출해 클릭·편집이 원본 패턴 셀로
   * 수렴하게 한다(행이 `row`(배치)와 `cell.row`(정체성)를 분리해 수렴하는 것과 동일). 미지정이면 cell.col.
   */
  identityCol?: number;
  /**
   * 순번 prefix 문자열(region.indexPrefix) — 대상 칼럼의 채워진 데이터 행(+editing 뼈대 행)에만 설정.
   * renderTableCell이 표시값/placeholder 앞에 그대로 prepend한다. minRows 패딩 빈 행엔 미설정.
   */
  indexPrefix?: string;
  /**
   * 이 셀이 유래한 반복영역의 `over` 키(반복영역 밴드 셀에만 설정, 헤더/정적/패딩 셀은 미설정).
   * 셀 content의 choiceGroup 노드가 dataDefs에서 DataDef를 찾아 options를 파생하는 열쇠
   * (renderTableCell이 ctx.cellOverKey로 전달). 반복 유래가 아니면 undefined → 파생 실패 fallback.
   */
  overKey?: string;
}

/**
 * 반복영역 전개 — repeatRegions를 data[over] 배열 길이만큼 복제하고,
 * region 아래 정적 셀은 늘어난 높이만큼 누적 시프트한다.
 *
 * 반환: 전개된 셀 목록 + 전개 후 총 행 수.
 */
/**
 * 그룹 병합 비교용 셀 "렌더 값" 추출.
 * binding(resolve된 scope에서 bindingKey 조회)/text만 비교 키로 사용한다.
 *
 * binding은 content보다 **먼저** 본다 — 이미지 병합 셀 패턴(binding=photoId + content=[image 노드],
 * admin 칼럼 매핑의 이미지 필드 특례가 생성)에서 렌더는 content(이미지)가 우선하지만 그룹 비교는
 * 행 데이터 값(binding)으로 해야 "같은 사진 N행"이 병합된다. binding 없이 content만 있는 셀은
 * 비교할 데이터 값이 없어 종전대로 병합 제외(undefined).
 */
function groupCellValue(ec: ExpandedCell): string | undefined {
  const { cell, scope } = ec;
  // text+binding 공존 셀의 비교 축도 binding(행 데이터 값) 우선 — text는 정적 장식일 수 있다.
  if (cell.binding) {
    const v = readBindingRaw(scope, cell.binding);
    if (v === null || v === undefined) return '';
    // 객체 값은 String()이 '[object Object]'로 붕괴해 서로 다른 행이 전부 동일 키가 된다
    // (admin 이미지 특례: 셀 binding=annotated 필드 + groupByCols 조합에서 다른 사진 행 오병합).
    // annotated는 url(사진 정체성), 그 외 객체는 JSON 직렬화로 비교한다.
    if (isAnnotatedImageValue(v)) return v.url;
    if (typeof v === 'object') return JSON.stringify(v);
    return String(v);
  }
  if (cell.content) return undefined;
  if (cell.text !== undefined) return cell.text;
  return undefined;
}

/**
 * 한 그룹 구분 칼럼의 연속 동일 값을 rowspan으로 병합한다(계층 병합).
 *
 * `colCells`는 대상 칼럼 셀만(호출측이 이미 필터·같은 밴드/단 범위) 넘긴다. 행 오름차순으로 모아
 * 연속 동일 값 run의 첫 셀에 rowSpanOverride=run길이를 설정하고 나머지 run 셀은 제거(covered —
 * occupied 격자가 자동 처리). 빈 값/단일 run은 병합 없음.
 *
 * **계층**: `ancestorKeyAt(row)`가 이 칼럼보다 상위(우선순위 앞)인 그룹 칼럼들의 같은 행 값 튜플을
 * 준다. run은 자기 값이 같아도 상위 튜플이 바뀌면 끊긴다(상위 대분류 경계에서 하위 소분류 run 종료).
 * 최상위 칼럼은 ancestorKeyAt이 항상 ''을 반환 → 기존 단일 칼럼 동작과 동일(회귀 0).
 *
 * 반환: `colCells`에서 제거 셀을 뺀 배열(호출측이 그 칼럼 범위를 이 결과로 교체).
 */
function applyGroupRowspan(
  colCells: ExpandedCell[],
  ancestorKeyAt: (row: number) => string,
): ExpandedCell[] {
  const sorted = [...colCells].sort((a, b) => a.row - b.row);
  if (sorted.length <= 1) return colCells;

  const removed = new Set<ExpandedCell>();
  let runStart = 0;
  while (runStart < sorted.length) {
    const startVal = groupCellValue(sorted[runStart]);
    const startAnc = ancestorKeyAt(sorted[runStart].row);
    let runEnd = runStart;
    // 값이 정의되고(빈 문자열 제외) 행이 연속(인접)이며 동일 값 + 동일 상위 튜플인 동안 run 확장.
    if (startVal !== undefined && startVal !== '') {
      while (
        runEnd + 1 < sorted.length &&
        sorted[runEnd + 1].row === sorted[runEnd].row + 1 &&
        groupCellValue(sorted[runEnd + 1]) === startVal &&
        ancestorKeyAt(sorted[runEnd + 1].row) === startAnc
      ) {
        runEnd++;
      }
    }
    const runLen = runEnd - runStart + 1;
    if (runLen > 1) {
      sorted[runStart].rowSpanOverride = runLen;
      for (let i = runStart + 1; i <= runEnd; i++) removed.add(sorted[i]);
    }
    runStart = runEnd + 1;
  }

  if (removed.size === 0) return colCells;
  return colCells.filter((ec) => !removed.has(ec));
}

export function expandTableCells(
  node: TableNode,
  data: RenderData,
  editing?: boolean,
): {
  cells: ExpandedCell[];
  totalRows: number;
  colCount: number;
  /**
   * 다단(columns≥2) 전개 시 effColCount 길이로 확장한 칼럼 폭 배열. 1단 패턴 폭을 단 수만큼
   * 가로로 복제한다(확장 col i의 폭 = node.colWidths[colStart + ((i-colStart) mod patternWidth)]).
   * 다단이 없으면 undefined → renderTable이 node.colWidths로 fallback. 값이 undefined인 칸은
   * width 미지정(auto). node.colWidths가 없거나 짧으면 해당 칸 undefined.
   */
  colWidths?: (number | string | undefined)[];
} {
  const regions = node.repeatRegions ?? [];
  // 정적 표(반복 없음): 모든 셀을 부모 scope로 그대로.
  if (regions.length === 0) {
    return {
      cells: node.cells.map((cell) => ({ cell, row: cell.row, scope: data })),
      totalRows: node.rowCount,
      colCount: node.colCount,
    };
  }

  // 모델 A: columns≥2 영역이 있으면 유효 colCount를 (colStart + 패턴폭×단수)로 확장한다.
  // → 운영자는 1단(패턴폭) 표만 그리고, 엔진이 단 수만큼 칼럼을 늘려 렌더한다.
  // colgroup(칼럼 폭)도 같은 기준으로 확장하기 위해, 최대 확장(effColCount)을 결정한
  // 다단 영역의 colStart/patternWidth를 함께 캡처한다(winMc).
  let effColCount = node.colCount;
  let winMc: { cStart: number; pw: number } | null = null;
  for (const region of regions) {
    const cols = region.columns ?? 1;
    if (cols < 2) continue;
    const cStart = region.colStart ?? 0;
    const cEnd = region.colEnd ?? node.colCount - 1;
    const pw = cEnd - cStart + 1;
    const eff = cStart + pw * cols;
    // 가장 멀리 확장하는 다단 영역을 폭 복제 기준으로 삼는다(첫 다단이면 무조건 채택).
    if (winMc === null || eff > effColCount) winMc = { cStart, pw };
    effColCount = Math.max(effColCount, eff);
  }

  // ── 밴드 모델 ──────────────────────────────────────────────────────────
  // 밴드 = 같은 행범위 [rowStart,rowEnd]를 공유하는 region들의 묶음. 한 밴드 안의
  // region들은 칼럼범위(colStart~colEnd)로 가로로 나란히 배치된다. 밴드 전개 길이(len)는
  // 그 밴드에 속한 영역들의 max(항목수, minRows). 영역마다 항목 수가 달라도 밴드 전체가
  // 같은 행 수만큼 펼쳐지고, 짧은 영역은 빈셀로 패딩된다.
  //
  // 단일 region(colStart/colEnd 미지정)이면 밴드 1개·영역 1개 → 기존 출력과 동일.
  // 세로 비중첩 다중 region이면 각자 다른 밴드(영역 1개씩) → shiftForRow 합산이 기존과 동일.
  type Band = {
    rowStart: number;
    rowEnd: number;
    /** 밴드 행 높이(rowEnd-rowStart+1) */
    height: number;
    /** 밴드 전개 길이 = max(영역 항목수, 영역 minRows) */
    len: number;
    regions: { region: TableRepeatRegion; items: unknown[] }[];
  };
  const bandMap = new Map<string, Band>();
  for (const region of regions) {
    const key = `${region.rowStart}:${region.rowEnd}`;
    const items = (data[region.over] as unknown[] | undefined) ?? [];
    let band = bandMap.get(key);
    if (!band) {
      band = {
        rowStart: region.rowStart,
        rowEnd: region.rowEnd,
        height: region.rowEnd - region.rowStart + 1,
        len: 0,
        regions: [],
      };
      bandMap.set(key, band);
    }
    band.regions.push({ region, items });
  }
  const bands = [...bandMap.values()].sort((a, b) => a.rowStart - b.rowStart);
  for (const band of bands) {
    band.len = Math.max(
      0,
      ...band.regions.map((r) => {
        // 모델 A: columns≥2면 단당 전개 행수 = ceil(항목수/단수). 단일 단이면 항목수(회귀).
        const cols = r.region.columns && r.region.columns > 1 ? r.region.columns : 1;
        const perCol = Math.ceil(r.items.length / cols);
        return Math.max(perCol, r.region.minRows ?? 0);
      }),
    );
    // 편집 캔버스: 데이터 0(+minRows 0)이라 밴드가 통째 사라질(len=0) 경우, 패턴 1행을 보존해
    // 셀이 빈 scope로 1줄 렌더되게 한다(바인딩/콘텐츠 유지 → {필드라벨} placeholder·편집 가능).
    // 비-editing은 0 유지(밴드 제거 — 회귀 0). minRows로 이미 len≥1인 밴드는 영향 없음.
    if (editing && band.len === 0) band.len = 1;
  }

  /**
   * 원본 행 r이 받는 누적 시프트 — r보다 "앞에서 끝난"(rowEnd < r) 밴드들의 높이 변화 합.
   * 밴드 전개 길이 len에 대해 (len-1)*height 변화(len=0이면 -height = 밴드 통째 제거).
   * strict(rowEnd < r)이라 인접 앞밴드의 확장은 포함되고, 자기 밴드는 제외(k 오프셋으로 별도 반영).
   */
  const shiftForRow = (r: number): number =>
    bands.reduce(
      (s, b) =>
        b.rowEnd < r ? s + (b.len === 0 ? -b.height : (b.len - 1) * b.height) : s,
      0,
    );

  /** 행 r이 속한 밴드(첫 매칭) */
  const bandOfRow = (r: number) =>
    bands.find((b) => r >= b.rowStart && r <= b.rowEnd);

  /** col이 region의 칼럼범위 [colStart, colEnd] 안인지(미지정=전폭). */
  const colIn = (region: TableRepeatRegion, col: number): boolean =>
    col >= (region.colStart ?? 0) && col <= (region.colEnd ?? node.colCount - 1);

  /**
   * region의 N단 헤더 복제 대상 행 범위 [start, end](양끝 포함) 또는 null(복제 안 함).
   *  - headerRows === 'none' → null
   *  - [start, end] 명시 → 그대로
   *  - 미지정 → rowStart>0 이면 바로 위 1행 [rowStart-1, rowStart-1], rowStart=0 이면 null.
   */
  const headerRowRange = (region: TableRepeatRegion): [number, number] | null => {
    if (region.headerRows === 'none') return null;
    if (Array.isArray(region.headerRows)) return region.headerRows;
    return region.rowStart > 0
      ? [region.rowStart - 1, region.rowStart - 1]
      : null;
  };

  const out: ExpandedCell[] = [];

  for (const cell of node.cells) {
    const band = bandOfRow(cell.row);
    if (!band) {
      // 정적 셀(헤더 등): 자기보다 앞에서 끝난 밴드들의 누적 시프트.
      const staticRow = cell.row + shiftForRow(cell.row);
      // 정적 셀의 rowspan이 [cell.row, cell.row+rowspan-1] 구간에 확장되는 밴드 행을
      // 포함하면, 밴드 전개분만큼 rowspan도 늘려야 아래 데이터행을 계속 덮는다.
      // 셀 바로 다음 원본 행의 확장 후 시작 위치 − 이 셀의 확장 후 시작 위치 = 새 rowspan.
      // 예) "금일 사고현황"(row0,rowspan2) + band(rowStart1,rowEnd1,len4,height1):
      //   shiftForRow(2)=(4-1)*1=3 → expandedRowspan=(0+2)+3−(0+0)=5 (header1+데이터4). ✓
      // band.len=0(제거)이면 shiftForRow가 음수라 rowspan 축소도 자연 처리.
      // origRowspan===1(대다수 셀)은 건드리지 않음(회귀 0).
      const origRowspan = cell.rowspan ?? 1;
      const expandedRowspan =
        origRowspan > 1
          ? cell.row + origRowspan + shiftForRow(cell.row + origRowspan) - staticRow
          : origRowspan;
      const withRowspan = (c: TableCell): TableCell =>
        origRowspan > 1 ? { ...c, rowspan: expandedRowspan } : c;
      // 모델 A: 이 정적 셀이 columns≥2 반복영역의 칼럼범위(colStart~colEnd) 안이면 다단 전개한다.
      //  - 전체폭 셀(colspan ≥ 1단 패턴폭: 타이틀·합계 제목 등): headerRows 범위와 무관하게
      //    항상 매칭 → 아래에서 colspan을 단 수만큼 확장(전체 유효폭을 덮음).
      //  - 부분 셀(colspan < 패턴폭: 헤더 라벨 "구분/항목/유무" 등): 그 영역의 헤더 행 범위
      //    (headerRowRange) 안일 때만 매칭 → 가로 N복제(3a 동작 유지). 범위 밖이면 매칭 실패 →
      //    아래 else로 떨어져 복제되지 않는다.
      const mcRegion = regions.find((r) => {
        if (r.columns === undefined || r.columns <= 1) return false;
        if (cell.col < (r.colStart ?? 0) || cell.col > (r.colEnd ?? node.colCount - 1))
          return false;
        const patternWidth =
          (r.colEnd ?? node.colCount - 1) - (r.colStart ?? 0) + 1;
        // 전체폭 셀은 headerRows 범위와 무관하게 다단 확장 대상.
        if ((cell.colspan ?? 1) >= patternWidth) return true;
        // 부분 셀(헤더 라벨 등)은 headerRows 범위 안에서만 복제.
        const range = headerRowRange(r);
        return range !== null && cell.row >= range[0] && cell.row <= range[1];
      });
      if (mcRegion) {
        const cols = mcRegion.columns as number;
        const patternWidth =
          (mcRegion.colEnd ?? node.colCount - 1) - (mcRegion.colStart ?? 0) + 1;
        const cspan = cell.colspan ?? 1;
        if (cspan >= patternWidth) {
          // 전체폭 셀(타이틀 등, 1단 폭 이상을 덮음) — 복제하지 않고 colspan을 단 수만큼 확장.
          out.push({
            cell: withRowspan({ ...cell, colspan: cspan + patternWidth * (cols - 1) }),
            row: staticRow,
            scope: data,
          });
        } else {
          // 1단 내 셀(헤더 라벨 등) — 가로 N복제(다단+rowspan 동시 케이스는 드물지만 일관 반영).
          for (let c = 0; c < cols; c++) {
            out.push({
              cell: withRowspan(
                c === 0 ? cell : { ...cell, col: cell.col + c * patternWidth },
              ),
              row: staticRow,
              scope: data,
            });
          }
        }
      } else {
        out.push({ cell: withRowspan(cell), row: staticRow, scope: data });
      }
      continue;
    }
    if (band.len === 0) continue; // 밴드 통째 제거(모든 영역 비고 minRows 0)
    const baseShift = shiftForRow(band.rowStart);
    const relRow = cell.row - band.rowStart;
    // 이 셀의 col을 칼럼범위로 포함하는 영역.
    const rg = band.regions.find((r) => colIn(r.region, cell.col));
    if (!rg) {
      // 밴드 행이지만 어느 영역 칼럼범위에도 없음(영역들이 폭을 타일링하면 미발생).
      // 방어적으로 정적 1회만 배치(밴드 시작 위치 기준).
      out.push({ cell, row: band.rowStart + baseShift + relRow, scope: data });
      continue;
    }
    const items = rg.items;
    // 모델 A 다단: 1단 패턴(colStart~colEnd) 셀을 가로로 columns번 복제(col += c*패턴폭) +
    // flow 규칙으로 데이터 분배. 단일 단(cols=1)이면 c=0·colC=cell.col·idx=k로 기존 동작(회귀).
    const cols = rg.region.columns && rg.region.columns > 1 ? rg.region.columns : 1;
    const flow = rg.region.flow ?? 'column';
    const patternWidth =
      (rg.region.colEnd ?? node.colCount - 1) - (rg.region.colStart ?? 0) + 1;
    const perCol = band.len;
    for (let c = 0; c < cols; c++) {
      const colC = cell.col + c * patternWidth;
      const baseCell = c === 0 ? cell : { ...cell, col: colC };
      // 우측 단(c>0) 복제 셀은 배치는 colC지만 편집 정체성은 원본 패턴 col로 수렴시킨다(행과 동일).
      const identityCol = c === 0 ? undefined : cell.col;
      // 순번 prefix: 이 영역의 indexPrefix가 이 셀의 (원본 패턴) 칼럼을 가리키면 항목 인덱스 기반 순번을
      // 부착한다(다단·페이지 분할에도 idx가 데이터 인덱스라 연속). 패딩 빈 행엔 미부착(아래 else 분기).
      const ip = rg.region.indexPrefix;
      const ipCol = ip !== undefined && cell.col === ip.col;
      for (let k = 0; k < band.len; k++) {
        const row = band.rowStart + baseShift + k * band.height + relRow;
        // 세로 채우기(column): 단을 세로로 다 채우고 다음 단 / 가로(row): 행마다 좌→우.
        const idx = flow === 'row' ? k * cols + c : k + c * perCol;
        if (idx < items.length) {
          out.push({
            cell: baseCell,
            row,
            scope: mergeRepeatScope(data, items[idx]),
            identityCol,
            indexPrefix: ipCol && ip ? formatIndexPrefix(idx, ip.style, ip.separator) : undefined,
            overKey: rg.region.over,
          });
        } else if (editing && items.length === 0) {
          // 편집 캔버스 + 데이터 0 밴드: 패턴 셀을 그대로 보존(바인딩/콘텐츠 유지)해 빈 scope(부모
          // data)로 1줄 렌더 → {필드라벨} placeholder·빈칸이 보이고 편집 가능. (band.len=1 보존 또는
          // minRows로 채운 행 모두 동일.) 일부 채운 밴드의 잔여 빈셀(items.length>0)은 아래 패딩으로.
          out.push({
            cell: baseCell,
            row,
            scope: data,
            identityCol,
            // editing 뼈대 행: idx(=0 등) 기반 '1.'류 순번을 보여 배치를 가늠하게 한다.
            indexPrefix: ipCol && ip ? formatIndexPrefix(idx, ip.style, ip.separator) : undefined,
            // 데이터 0 뼈대 행도 반복영역 유래이므로 over 키를 실어 choiceGroup이 dataDef options를
            // 편집 캔버스에서 파생(빈 scope라 체크는 안 되나 옵션 박스는 보여 저작 확인 가능).
            overKey: rg.region.over,
          });
        } else {
          // 빈셀 패딩 — 좌표/스타일은 보존하되 내용만 비운다.
          out.push({
            cell: {
              ...baseCell,
              content: undefined,
              text: '',
              binding: undefined,
              aggregate: undefined,
              compute: undefined,
            },
            row,
            scope: data,
            identityCol,
          });
        }
      }
    }
  }

  // 전개 후 총 행 수 = 원본 rowCount + 모든 밴드의 (len-1)*height. len=0이면 밴드 높이만큼 제거.
  let totalRows = node.rowCount;
  for (const b of bands) {
    totalRows += b.len === 0 ? -b.height : (b.len - 1) * b.height;
  }

  // 그룹 rowspan 병합 — 밴드별·영역별로, 그룹 구분 칼럼(effectiveGroupByCols, 계층 순서)이 지정된
  // 영역의 *실제 채운 행*(items.length, 패딩 제외) + 그 영역 칼럼에 한정해 적용.
  let merged = out;
  for (const band of bands) {
    const baseShift = shiftForRow(band.rowStart);
    for (const { region, items } of band.regions) {
      const baseGroupCols = effectiveGroupByCols(region);
      if (baseGroupCols.length === 0 || items.length === 0) continue;
      // 모델 A 다단: 각 단(c)의 구분 칼럼(base + c*패턴폭)을, 그 단에 분배된 항목 수만큼의
      // 행 범위에서 병합. 단일 단이면 c=0·col=base·rowsC=items.length(회귀).
      const cols = region.columns && region.columns > 1 ? region.columns : 1;
      const flow = region.flow ?? 'column';
      const patternWidth =
        (region.colEnd ?? node.colCount - 1) - (region.colStart ?? 0) + 1;
      const perCol = band.len;
      for (let c = 0; c < cols; c++) {
        const rowsC =
          flow === 'row'
            ? c < items.length
              ? Math.ceil((items.length - c) / cols)
              : 0
            : Math.max(0, Math.min(perCol, items.length - c * perCol));
        if (rowsC === 0) continue;
        const lo = band.rowStart + baseShift;
        const hi = lo + rowsC * band.height - 1;
        // 이 단(c)의 그룹 칼럼들(계층 순서). 상위 칼럼 값을 병합 전 원본(out)에서 읽어 하위 run의
        // 경계 판정에 쓴다(상위 병합 결과와 무관 — 논리 값은 행마다 불변).
        const stratumCols = baseGroupCols.map((g) => g + c * patternWidth);
        const valueAt = new Map<string, string>();
        for (const ec of out) {
          if (ec.row < lo || ec.row > hi) continue;
          if (!stratumCols.includes(ec.cell.col)) continue;
          valueAt.set(`${ec.cell.col}:${ec.row}`, groupCellValue(ec) ?? '');
        }
        for (let h = 0; h < stratumCols.length; h++) {
          const col = stratumCols[h];
          const ancestorCols = stratumCols.slice(0, h);
          const ancestorKeyAt = (row: number): string =>
            JSON.stringify(ancestorCols.map((ac) => valueAt.get(`${ac}:${row}`) ?? ''));
          const inRange = merged.filter(
            (ec) => ec.cell.col === col && ec.row >= lo && ec.row <= hi,
          );
          const survivors = applyGroupRowspan(inRange, ancestorKeyAt);
          if (survivors.length !== inRange.length) {
            const kept = new Set(survivors);
            merged = merged.filter(
              (ec) =>
                ec.cell.col !== col || ec.row < lo || ec.row > hi || kept.has(ec),
            );
          }
        }
      }
    }
  }

  // 다단(winMc)이면 colgroup 폭도 effColCount 길이로 확장: 1단 패턴(cStart~cStart+pw-1)의
  // node.colWidths 값을 단 수만큼 가로로 복제한다. cStart 앞 칼럼은 원본 그대로, 패턴 범위부터는
  // (i-cStart) mod pw 로 패턴 폭을 순환 복제. node.colWidths가 없거나 짧으면 undefined(=auto).
  let colWidths: (number | string | undefined)[] | undefined;
  if (winMc) {
    const { cStart, pw } = winMc;
    colWidths = Array.from({ length: effColCount }, (_, i) =>
      i < cStart
        ? node.colWidths?.[i]
        : node.colWidths?.[cStart + ((i - cStart) % pw)],
    );
  }

  return {
    cells: merged,
    totalRows: Math.max(totalRows, 0),
    colCount: effColCount,
    colWidths,
  };
}

// ── 표 페이지 채움: 잔여 slack을 빈 괘선 행으로 페인팅 ─────────────────────────
// pageFill 표가 받은 잔여 높이(ctx.fillSlacks)를 group/stack처럼 통짜 여백(outer 확장)으로 두지 않고,
// 구 SMS 위험성평가 표 동형으로 데이터 행 아래를 빈 괘선 행으로 채운다(순수 시각 — 데이터 아님).

/** 엔진 기본 채움행 높이 근사(px) — defaultCellStyle(11pt≈14.67px) 한 줄 line-box + 상하 패딩(≈2px) + 보더. */
export const DEFAULT_FILL_ROW_HEIGHT = 24;

/**
 * 채움행 1행 높이 결정 — node.fillRowHeight(양수) 우선, 미지정이면 밴드 자연 행 높이 근사:
 * 반복영역 패턴 행(최하단 밴드 rowStart, 반복영역 없으면 마지막 행)의 rowHeights 숫자값,
 * 그것도 없으면 엔진 기본 근사(DEFAULT_FILL_ROW_HEIGHT).
 */
export function resolveFillRowHeight(node: TableNode): number {
  if (typeof node.fillRowHeight === 'number' && node.fillRowHeight > 0) return node.fillRowHeight;
  const regions = node.repeatRegions ?? [];
  const patternRow =
    regions.length > 0 ? Math.max(...regions.map((rg) => rg.rowStart)) : node.rowCount - 1;
  const h = node.rowHeights?.[patternRow];
  if (typeof h === 'number' && h > 0) return h;
  return DEFAULT_FILL_ROW_HEIGHT;
}

/**
 * 채움행 페인팅 계획(순수) — 행 수 = floor(slack / rowHeight), 끝자락 나머지는 마지막 빈 행 높이에
 * 흡수한다(물리 확장 합 = slack 정확 일치). slack≤0 또는 slack < rowHeight(1행도 못 만듦)이면
 * count 0(페인팅 생략 — 기존 pageFill no-op 규칙과 일관).
 */
export function planTableFillRows(
  slack: number,
  rowHeight: number,
): { count: number; lastRowHeight: number } {
  if (!(rowHeight > 0) || !(slack > 0)) return { count: 0, lastRowHeight: 0 };
  const count = Math.floor(slack / rowHeight);
  if (count <= 0) return { count: 0, lastRowHeight: 0 };
  return { count, lastRowHeight: rowHeight + (slack - count * rowHeight) };
}

/** 채움행 열 구조 1칸 — 원본 패턴 셀에서 추출한 (col, colspan, 괘선/배경 스타일). */
export interface FillRowPatternCell {
  col: number;
  colspan: number;
  style: CSSProperties;
}

/**
 * 채움행의 열 구조(칼럼 경계·테두리) 추출 — 밴드(반복영역) 칼럼 구조와 동일하게 그린다.
 *  - 반복영역 있음: **최하단 밴드**(rowStart 최대)의 패턴 첫 행 셀들의 col/colspan/테두리(+배경)를
 *    사용하고, 다단(columns≥2)이면 expandTableCells와 동일 규칙으로 가로 복제(col + c×패턴폭).
 *    (채움행은 데이터 행 아래에 이어지므로 데이터 밴드의 괘선을 계속 긋는 것이 구 SMS 표 동형.)
 *  - 반복영역 없음(정적 표): 마지막 행에서 시작하는 셀들의 구조를 그대로 사용(마지막 행 구조 기준).
 * rowspan은 채움행에 무의미(행마다 독립 괘선) — colspan만 취한다. 위 행 rowspan에 덮여 커버되지
 * 않는 칼럼은 렌더 측에서 첫 패턴 셀 스타일로 fallback 페인팅한다(근사).
 */
export function computeFillRowPattern(node: TableNode): FillRowPatternCell[] {
  const styleOf = (cell: TableCell): CSSProperties => {
    const s: CSSProperties = {};
    Object.assign(s, resolveCellBorder(cell.style?.border, cell.style?.borderOverrides));
    if (cell.style?.background !== undefined) s.background = cell.style.background;
    return s;
  };
  const out: FillRowPatternCell[] = [];
  const regions = node.repeatRegions ?? [];
  if (regions.length === 0) {
    const lastRow = node.rowCount - 1;
    for (const cell of node.cells) {
      if (cell.row !== lastRow) continue;
      out.push({ col: cell.col, colspan: cell.colspan ?? 1, style: styleOf(cell) });
    }
  } else {
    const patternRow = Math.max(...regions.map((rg) => rg.rowStart));
    for (const region of regions) {
      if (region.rowStart !== patternRow) continue;
      const cStart = region.colStart ?? 0;
      const cEnd = region.colEnd ?? node.colCount - 1;
      const cols = region.columns && region.columns > 1 ? region.columns : 1;
      const patternWidth = cEnd - cStart + 1;
      for (const cell of node.cells) {
        if (cell.row !== patternRow || cell.col < cStart || cell.col > cEnd) continue;
        const style = styleOf(cell);
        for (let c = 0; c < cols; c += 1) {
          out.push({ col: cell.col + c * patternWidth, colspan: cell.colspan ?? 1, style });
        }
      }
    }
  }
  return out.sort((a, b) => a.col - b.col);
}

function renderTable(node: TableNode, data: RenderData, ctx: RenderContext, parent?: ParentContext): ReactNode {
  const tableStyle: CSSProperties = {
    ...nodeLayoutToStyle(node.layout, parent),
    borderCollapse: 'collapse',
    // fixed: 칼럼 너비를 colWidths(미지정 칸은 균등 분배)로 고정 — 내용이 길어도 칼럼이
    // 늘어나지 않고 셀 안에서 줄바꿈된다(auto는 내용에 맞춰 칼럼이 커짐).
    tableLayout: 'fixed',
  };
  // table-layout:'fixed'는 표 너비가 **definite**일 때만 유효하다(CSS 스펙). width가 미지정이면
  // (그룹 주축에서 nodeLayoutToStyle이 width를 내지 않는다) 또는 'auto'(hug)면 브라우저가 fixed를
  // auto 알고리즘으로 degrade → 칼럼이 내용 intrinsic 폭으로 재분배돼 출렁인다(예: width 미지정
  // 2열 표에 46px 체크박스 삽입 시 [289,69]로 쏠림, 정상 fill 표는 [359,359] 균등). 그런 불안정 표는
  // 부모 박스를 채우는(width:100%) 것으로 통일해 definite를 보장한다 — flex row 부모 안에서도 형제와
  // flex 공간을 나눠 가지므로 시각 회귀 없이 칼럼 균등이 복원된다.
  // ⚠ flex 주축 채움(fill → flexGrow1+flexBasis0)도 **보정 대상** — flex가 박스 크기는 주지만 CSS의
  // fixed 판정은 width *속성* 기준이라 width:auto면 여전히 degrade한다(2026-07-03 실측: flex-basis:0
  // 표에 46px 체크박스 삽입 시 칼럼 [289,69] 쏠림). width:100%는 flex-basis:0이 주축 크기를 계속
  // 지배하므로 박스 크기에 영향 없이 fixed만 복원한다. 예외 1가지:
  //  - **전 칼럼 colWidths가 구체값(number/px/%)인 hug 표**: auto 알고리즘에서도 칼럼 폭이 지정값으로
  //    결정돼 안정적이며, 폭 합만큼만 차지하는 것이 저작 의도(예: 우측 소형 결재란 표) — 100% 강제 시
  //    기존 템플릿 외형이 바뀌므로 제외. (내용이 지정 폭을 넘치면 그 칼럼만 커짐 — hug 의미론과 일치.)
  const colWidthsAllConcrete =
    (node.colWidths?.length ?? 0) >= node.colCount &&
    node.colWidths!.slice(0, node.colCount).every((w) => w !== undefined && w !== null && w !== 'auto');
  if (
    (tableStyle.width === undefined || tableStyle.width === 'auto') &&
    !colWidthsAllConcrete
  ) {
    tableStyle.width = '100%';
  }
  if (node.tablePolicy === 'whole') tableStyle.breakInside = 'avoid';

  // 표 자체(외곽) 테두리 — 전체 셀 테두리와 별개(예 외곽 1.5px / 셀 1px). resolveCellBorder로 base+변별 합성.
  // border-collapse:collapse 하에서 table 엘리먼트 테두리가 외곽으로 동작하며, 셀 테두리와 겹치는 변은
  // CSS collapse 규칙(더 굵은 쪽 승리)으로 병합된다. 미지정=없음(기존 동작, 회귀 0).
  Object.assign(tableStyle, resolveCellBorder(node.outerBorder, node.outerBorderOverrides));

  // 편집 캔버스(ctx.editing)도 미리보기/출력과 동일하게 expandTableCells로 반복영역/다단을 전개해
  // 실값·다단을 보여준다. 단 editing 인자가 "데이터 0 밴드"를 패턴 1행으로 보존하므로(밴드 통째
  // 제거 대신) 데이터가 비어도 패턴 행이 사라지지 않는다. 셀 선택은 renderTableCell이 *원본 셀*
  // 좌표(data-composer-cell = cell.row/col)로 방출하므로, 전개로 늘어난 행/다단 generated 셀을
  // 클릭해도 선택이 원본 패턴 셀로 수렴한다(편집은 자연히 패턴 행에 한정 — 좌표 매핑 변경 불필요).
  const { cells, totalRows, colCount, colWidths: expandedColWidths } =
    expandTableCells(node, data, ctx.editing);

  // colgroup 칼럼 폭: 다단 전개면 expandTableCells가 effColCount 길이로 확장한 폭 배열을
  // 준다(1단 폭을 단 수만큼 복제) → <col> 개수를 colCount와 일치시킨다. 다단이 아니면(undefined)
  // 기존 node.colWidths 그대로 사용(회귀 0). 값이 undefined인 칸은 width 미지정(auto).
  const colgroup =
    expandedColWidths && expandedColWidths.length > 0 ? (
      <colgroup>
        {Array.from({ length: colCount }, (_, i) => {
          const w = expandedColWidths[i];
          return (
            <col
              key={i}
              style={
                w === undefined || w === null
                  ? undefined
                  : { width: typeof w === 'number' ? `${w}px` : w }
              }
            />
          );
        })}
      </colgroup>
    ) : node.colWidths && node.colWidths.length > 0 ? (
      <colgroup>
        {node.colWidths.map((w, i) => (
          <col key={i} style={{ width: typeof w === 'number' ? `${w}px` : w }} />
        ))}
      </colgroup>
    ) : null;

  // 2D 그리드 점유 맵: span에 덮인 슬롯을 표시(셀 미생성).
  const occupied: boolean[][] = Array.from({ length: totalRows }, () =>
    Array.from({ length: colCount }, () => false),
  );

  // 행별 셀 버킷(col 오름차순 정렬).
  const rowBuckets: ExpandedCell[][] = Array.from({ length: totalRows }, () => []);
  for (const ec of cells) {
    if (ec.row < 0 || ec.row >= totalRows) continue;
    rowBuckets[ec.row].push(ec);
  }
  for (const bucket of rowBuckets) bucket.sort((a, b) => a.cell.col - b.cell.col);

  const baseRowStyle: CSSProperties =
    node.tablePolicy === 'rows' ? { breakInside: 'auto' } : {};

  // 행 높이(rowHeights): 숫자=px, 'auto'/미지정=내용, '%'/문자열=그대로(stretch 시 균등/비율).
  const rowHeightFor = (r: number): string | undefined => {
    const h = node.rowHeights?.[r];
    if (h === undefined || h === 'auto') return undefined;
    return typeof h === 'number' ? `${h}px` : h;
  };

  // 페이지 분할(rowRanges): 이 표에 범위가 지정되면 그 행 범위만 <tr>로 방출한다. rangeStart가 안전
  // 경계(rowspan 미관통)면 아래 continuation 주입은 no-op(회귀 0). 묶음행을 경계에서 쪼개는 OFF 모드
  // /오버사이즈 강제 split에서는 rangeStart가 rowspan 내부를 관통할 수 있어 continuation 재방출이 필요하다.
  // 조회 키는 itemRanges와 동형으로 **인스턴스 경로** 확장(ctx.instancePath 주석) — 반복 항목 안의
  // 표는 인스턴스마다 다른 행 범위를 가질 수 있다(분할 경계 3순위 = 표 행). 최상위 표는
  // instancePath가 없어 키 = node.id 그대로(회귀 0).
  const range = ctx.rowRanges?.[ctx.instancePath ? `${ctx.instancePath}/${node.id}` : node.id];
  const rangeStart = range ? Math.max(0, range[0]) : 0;
  const rangeEnd = range ? Math.min(totalRows, range[1]) : totalRows;

  // 페이지 경계(rangeStart)를 관통하는 rowspan/병합 셀(원본 시작행 < rangeStart < 원본끝)을 rangeStart
  // 위치에 continuation 셀로 재방출한다 — 안 그러면 이 셀은 rangeStart.. 범위에서 방출되지 않아 빈 칸이
  // 된다. 남은 span은 rangeEnd로 clamp하고 내용/스타일/scope는 원본과 동일하다(병합 라벨 반복=판독성).
  // rowRanges 미지정(rangeStart===0)이면 관통이 없어 이 블록은 건너뛴다(회귀 0).
  if (rangeStart > 0 && rangeStart < totalRows) {
    const contBucket = rowBuckets[rangeStart];
    let injected = false;
    for (const ec of cells) {
      const effSpan = ec.rowSpanOverride ?? ec.cell.rowspan ?? 1;
      if (effSpan <= 1) continue;
      const cellEnd = ec.row + effSpan; // 반열림 끝
      if (ec.row < rangeStart && cellEnd > rangeStart) {
        const contSpan = Math.min(cellEnd, rangeEnd) - rangeStart;
        if (contSpan > 0) {
          contBucket.push({ ...ec, row: rangeStart, rowSpanOverride: contSpan, isContinuation: true });
          injected = true;
        }
      }
    }
    if (injected) contBucket.sort((a, b) => a.cell.col - b.cell.col);
  }

  // 반복영역별 묶음(그룹 구분 칼럼) 옵션을 (다단 확장 포함) 칼럼 집합으로 변환한다.
  //  - groupContinuationCols: region.groupContinuationMark ON → 그 영역의 **전 그룹 칼럼** continuation 셀에 ` (계속)`.
  //  - keepTogetherCols: region.groupStartOnNewPage ON(+세로 다단 아님) → 그 영역 그룹을 페이지 경계에서
  //    쪼개지 않는다. 계층 병합에서는 **첫(주) 칼럼 그룹 기준만**(결정 §3) — 그 칼럼의 병합셀에만
  //    data-composer-rowspan-full(원본 span)을 방출해 measure가 그 그룹만 안전 경계로 삼게 한다
  //    (나머지 병합/그룹은 자유 컷). 다단(columns≥2)이면 각 단의 base+c*패턴폭.
  const groupContinuationCols = new Set<number>();
  const keepTogetherCols = new Set<number>();
  for (const region of node.repeatRegions ?? []) {
    const groupCols = effectiveGroupByCols(region);
    if (groupCols.length === 0) continue;
    const cols = region.columns && region.columns > 1 ? region.columns : 1;
    const patternWidth = (region.colEnd ?? node.colCount - 1) - (region.colStart ?? 0) + 1;
    // 세로 채우기 다단은 단별 그룹이 물리 행에서 어긋나 keep-together 미지원.
    const isVerticalMultiColumn = cols > 1 && (region.flow ?? 'column') === 'column';
    const primaryCol = groupCols[0];
    for (let c = 0; c < cols; c += 1) {
      const offset = c * patternWidth;
      // continuation 표시는 전 그룹 칼럼에.
      if (region.groupContinuationMark) {
        for (const g of groupCols) groupContinuationCols.add(g + offset);
      }
      // keep-together는 첫(주) 칼럼 그룹 기준만.
      if (region.groupStartOnNewPage && !isVerticalMultiColumn) {
        keepTogetherCols.add(primaryCol + offset);
      }
    }
  }

  const trs: ReactNode[] = [];
  for (let r = rangeStart; r < rangeEnd; r++) {
    const rh = rowHeightFor(r);
    const rowStyle: CSSProperties = rh !== undefined ? { ...baseRowStyle, height: rh } : baseRowStyle;
    const tds: ReactNode[] = [];
    let c = 0;
    const bucket = rowBuckets[r];
    let bi = 0;
    while (c < colCount) {
      if (occupied[r][c]) {
        c++;
        continue;
      }
      const ec = bucket[bi];
      if (ec && ec.cell.col === c) {
        // region N=0 제거 등으로 행/열이 줄면 span이 grid 범위를 넘을 수 있어 clamp.
        // groupByCol 병합이 산출한 rowSpanOverride가 있으면 cell.rowspan보다 우선.
        const rs = Math.min(ec.rowSpanOverride ?? ec.cell.rowspan ?? 1, rangeEnd - r);
        const cs = Math.min(ec.cell.colspan ?? 1, colCount - c);
        // span에 덮이는 슬롯 점유 표시(자기 자신 제외).
        for (let dr = 0; dr < rs; dr++) {
          for (let dc = 0; dc < cs; dc++) {
            if (dr === 0 && dc === 0) continue;
            const rr = r + dr;
            const cc = c + dc;
            if (rr < totalRows && cc < colCount) occupied[rr][cc] = true;
          }
        }
        // continuation 병합 라벨(groupByCol 칼럼 + groupContinuationMark ON)이면 ` (계속)` 접미어.
        const continuationSuffix =
          ec.isContinuation && groupContinuationCols.has(ec.cell.col)
            ? ' (계속)'
            : undefined;
        // 셀 식별 좌표는 *원본 셀*의 (row,col) — 반복 전개로 시프트된 절대 r/c가 아니라
        // 원본 트리에서 셀을 찾는 좌표를 방출해야 updateCell이 정확히 매칭한다.
        tds.push(
          renderTableCell(
            ec.cell,
            `${r}-${c}`,
            ec.scope,
            ctx,
            rs,
            cs,
            node.id,
            ec.cell.row,
            // 다단 우측 단 복제 셀은 정체성 좌표(원본 패턴 col)를 방출해 클릭·편집이 패턴 셀로 수렴.
            ec.identityCol ?? ec.cell.col,
            continuationSuffix,
            // keep-together(groupStartOnNewPage) 대상 묶음 그룹 셀에만 원본 span을 방출한다.
            // measure는 이 셀들만 안전 경계로 삼는다(나머지 병합/그룹은 자유 컷). continuation 셀 제외.
            !ec.isContinuation &&
              ec.rowSpanOverride !== undefined &&
              keepTogetherCols.has(ec.cell.col)
              ? ec.rowSpanOverride
              : undefined,
            ec.indexPrefix,
            ec.overKey,
          ),
        );
        bi++;
        c += cs;
      } else {
        // 빈 좌표 → 빈 <td>
        tds.push(<td key={`${r}-${c}-empty`} />);
        c++;
      }
    }
    trs.push(
      // data-composer-row: 전개 후 절대 행 인덱스(tableId::r). admin 페이지 분할이 분할된 상태에서도
      // 각 행 높이(offsetHeight)와 rowspan(td.rowSpan+closest tr)을 측정하는 데 쓴다(web/PDF 무해).
      <tr key={r} data-composer-row={`${node.id}::${r}`} style={rowStyle}>
        {tds}
      </tr>,
    );
  }

  // ── 페이지 채움(표): 계획이 준 잔여 slack을 빈 괘선 행으로 페인팅 ──────────────
  // 빈 행은 데이터가 아니다 — data-composer-row/cell 미방출(선택·편집·측정 제외, 순수 시각).
  // 측정(measureTable)은 data-composer-row 행 높이 합산이라 채움 행이 있어도 **자연 높이**를
  // 읽는다(측정 불변식 — 결정 기록 2026-07-02-composer-page-fill-node 제1원칙: 확장 높이를
  // 측정 입력으로 되먹이지 않음). data-composer-table-rows(총 행수)도 불변. planRowPages(행 분할)
  // 미접촉 — rowRanges로 클립된 조각(rangeEnd<totalRows)에는 페인팅하지 않는다(계획도 통짜
  // 배치에만 slack을 부여). td는 빈 콘텐츠(내용 없음)라 tr height가 정확히 지배 → 물리 확장 = slack.
  // pageFill 단일 흡수 표 또는 fillSplitGroup 분배 표 모두 배정 slack을 빈 괘선 행으로 채운다
  // (계획이 fillSlacks에 배정높이−자연높이를 넣음). 둘 다 통짜 배치(rangeEnd===totalRows)에만 부여.
  // 조회 키는 rowRanges와 동형으로 **인스턴스 경로** 확장(ctx.instancePath 주석) — 반복 단위 안의
  // 채움 표는 같은 template 노드가 단위 수만큼 렌더되므로, 경로 키가 아니면 한 낱장의 slack이 모든
  // 단위 인스턴스에 적용된다(결정 2026-09-03). 최상위 표는 instancePath가 없어 키 = node.id
  // 그대로(회귀 0). rangeEnd===totalRows 게이트가 행 분할 조각(계획도 부여하지 않음)을 거른다.
  const fillSlack =
    (node.layout?.pageFill === true || node.layout?.fillSplitGroup != null) &&
    rangeEnd === totalRows
      ? ctx.fillSlacks?.[ctx.instancePath ? `${ctx.instancePath}/${node.id}` : node.id]
      : undefined;
  if (fillSlack !== undefined && fillSlack > 0) {
    const fh = resolveFillRowHeight(node);
    const { count, lastRowHeight } = planTableFillRows(fillSlack, fh);
    if (count > 0) {
      const pattern = computeFillRowPattern(node);
      // 패턴이 커버 못 한 칼럼(위 행 rowspan에 덮인 슬롯 등)의 fallback 괘선 — 첫 패턴 셀 스타일 근사.
      const fallbackStyle = pattern[0]?.style ?? {};
      for (let k = 0; k < count; k += 1) {
        const h = k === count - 1 ? lastRowHeight : fh;
        const tds: ReactNode[] = [];
        let c = 0;
        let pi = 0;
        while (c < colCount) {
          const pc = pattern[pi];
          if (pc && pc.col === c) {
            const cs = Math.min(pc.colspan, colCount - c);
            tds.push(<td key={`f-${k}-${c}`} colSpan={cs > 1 ? cs : undefined} style={pc.style} />);
            pi += 1;
            c += cs;
          } else if (pc && pc.col < c) {
            pi += 1; // 중복/겹침 좌표 방어(스킵)
          } else {
            tds.push(<td key={`f-${k}-${c}`} style={fallbackStyle} />);
            c += 1;
          }
        }
        trs.push(
          <tr key={`fill-${k}`} data-composer-fill-row={node.id} style={{ height: `${h}px` }}>
            {tds}
          </tr>,
        );
      }
    }
  }

  return (
    // data-composer-table-rows: 전개 후 진짜 총 행수(rowRanges로 클립돼도 불변). admin 페이지 분할이
    // 클립으로 새 행이 누락됐는지 판별해 stale plan을 리셋하는 데 쓴다(web/PDF 무해).
    <table
      key={node.id}
      data-composer-node-id={node.id}
      data-composer-table-rows={totalRows}
      style={tableStyle}
    >
      {colgroup}
      <tbody>{trs}</tbody>
    </table>
  );
}

/**
 * 집계 셀 산출 — 같은 scope의 배열(over)을 fn으로 집계해 표시값을 낸다(선언형, 임의식 아님).
 *
 * count는 항목 수(arr.length), 그 외는 항목 수치를 모아 집계한다:
 *   - field 지정 시 항목의 field 경로 값, 미지정 시 항목 자체를 Number()로 변환(NaN은 제외).
 *   - sum=합(빈 0) / avg=합/개수(개수 0이면 0) / min·max=빈 배열이면 0.
 * 결과에 format(unit 등)을 적용해 반환한다(예: sum=20 + unit '명' → '20 명').
 *
 * format 적용 **전** 수치가 필요한 호출부(rangeMap 배지의 구간 판정 등)는
 * computeCellAggregateValue를 쓰고 스스로 applyFormat을 부른다 — 판정과 표시가 같은 수치를 본다.
 */
export function computeCellAggregate(agg: CellAggregate, scope: RenderData): unknown {
  return applyFormat(computeCellAggregateValue(agg, scope), agg.format, scope);
}

/** 집계 셀의 **format 적용 전** 수치(위 computeCellAggregate의 산출 단계). */
export function computeCellAggregateValue(agg: CellAggregate, scope: RenderData): number {
  const raw = getByPath(scope, agg.over);
  const arr = Array.isArray(raw) ? raw : [];
  const nums = arr
    .map((it) => (agg.field ? getByPath(it, agg.field) : it))
    .map((v) => Number(v))
    .filter((n) => !Number.isNaN(n));

  let result: number;
  switch (agg.fn) {
    case 'count':
      result = arr.length;
      break;
    case 'sum':
      result = nums.reduce((a, b) => a + b, 0);
      break;
    case 'avg':
      result = nums.length === 0 ? 0 : nums.reduce((a, b) => a + b, 0) / nums.length;
      break;
    case 'min':
      result = nums.length === 0 ? 0 : Math.min(...nums);
      break;
    case 'max':
      result = nums.length === 0 ? 0 : Math.max(...nums);
      break;
  }

  return result;
}

/** 입력 1개의 수치값 — const 우선, 아니면 binding.bindingKey를 scope에서 읽어 Number(). 비숫자/누락=0. */
function computeInputValue(input: CellCompute['inputs'][number], scope: RenderData): number {
  if (input.const !== undefined) {
    return Number.isFinite(input.const) ? input.const : 0;
  }
  if (input.binding) {
    // 표 셀 binding과 동일 경로(이미 resolve된 RenderData에서 bindingKey 조회 + 하위호환 폴백).
    const raw = readBindingRaw(scope, input.binding);
    const num = Number(raw);
    return Number.isFinite(num) ? num : 0;
  }
  return 0;
}

/**
 * 계산 셀 산출 — named-input(어느 소스든 resolve된 값) 또는 상수를 op/안전수식으로 조합한다.
 *
 * 셀 좌표 참조(=SUM(A1:A2))가 아니라 데이터-측 계산이다(content가 전 소스 통합 → cross-source).
 *   - mode='expr': vars={name:value} 구성 후 evalSafeExpr(expr). expr 비거나 throw → 빈값.
 *   - mode='op': 입력값 배열에 op 적용. sum=Σ / subtract=v0−v1−… / multiply=Π /
 *     divide=v0/v1/… / avg=Σ/n(n=0→0) / min·max(빈→0).
 * 결과가 유한수가 아니면(NaN/±Infinity) 빈 문자열, 유효하면 format(unit 등) 적용 후 반환.
 *
 * format 적용 **전** 수치가 필요한 호출부(rangeMap 배지의 구간 판정 등)는 computeCellComputeValue를
 * 쓰고 스스로 applyFormat을 부른다 — 판정과 표시가 같은 수치를 본다.
 */
export function computeCellCompute(compute: CellCompute, scope: RenderData): unknown {
  const value = computeCellComputeValue(compute, scope);
  if (value === '') return '';
  return applyFormat(value, compute.format, scope);
}

/**
 * 계산 셀의 **format 적용 전** 수치(위 computeCellCompute의 산출 단계).
 * 산출 불가(expr throw/빈 식·미지원 op)나 비유한수(NaN/±Infinity)는 빈 문자열.
 */
export function computeCellComputeValue(compute: CellCompute, scope: RenderData): number | '' {
  let result: number;

  if (compute.mode === 'expr') {
    const vars: Record<string, number> = {};
    for (const input of compute.inputs) {
      vars[input.name] = computeInputValue(input, scope);
    }
    try {
      result = evalSafeExpr(compute.expr ?? '', vars);
    } catch {
      return '';
    }
  } else {
    const values = compute.inputs.map((input) => computeInputValue(input, scope));
    switch (compute.op) {
      case 'sum':
        result = values.reduce((a, b) => a + b, 0);
        break;
      case 'subtract':
        result = values.length === 0 ? 0 : values.slice(1).reduce((a, b) => a - b, values[0]);
        break;
      case 'multiply':
        result = values.reduce((a, b) => a * b, 1);
        break;
      case 'divide':
        result = values.length === 0 ? 0 : values.slice(1).reduce((a, b) => a / b, values[0]);
        break;
      case 'avg':
        result = values.length === 0 ? 0 : values.reduce((a, b) => a + b, 0) / values.length;
        break;
      case 'min':
        result = values.length === 0 ? 0 : Math.min(...values);
        break;
      case 'max':
        result = values.length === 0 ? 0 : Math.max(...values);
        break;
      default:
        return '';
    }
  }

  // NaN/±Infinity는 빈값으로(renderTableCell의 빈 셀 가드가 nbsp 처리).
  if (!Number.isFinite(result)) return '';
  return result;
}

/**
 * rangeMap 표시(알약 배지 + 병기)를 표 셀의 두 갈래로 나눈다 — 값 소스 3경로(aggregate/compute/
 * binding)가 같은 규약을 쓰게 하는 공용 지점.
 *
 * - **알약 노드**면 badgeNode로도 들려 나간다 → 호출부가 순번 prefix·` (계속)` 접미어를 알약
 *   **바깥**에 붙인다(알약 안에 순번이 들어가면 배지가 값이 아니라 문장이 된다).
 * - **문자열**(병기만 있거나 배지가 미매칭으로 degrade한 경우)이면 badgeNode는 null — 종전 문자열
 *   경로 그대로라 prefix/접미어가 문자열로 이어 붙는다.
 */
function rangeCellContent(
  raw: unknown,
  format: BindingFormat | undefined,
  scope: RenderData,
  text: string,
): { content: ReactNode; badgeNode: ReactNode | null } {
  const node = rangeDisplayNode(raw, format, scope, text);
  if (node === undefined) return { content: text, badgeNode: null };
  if (typeof node === 'string') return { content: node, badgeNode: null };
  return { content: node, badgeNode: node };
}

/**
 * 반복영역 photo 칼럼(dataDef widget:'photo') binding 셀의 표시 URL 판정.
 *
 * photo 칼럼 값 정본은 업로드된 URL 문자열이라, 일반 binding 텍스트 경로로 흘리면 본문 표 셀에
 * blob:/https: URL 원문이 그대로 보인다(작성폼 readonly 셀이 photoReadonlyCellNode로 막는 것과
 * 같은 결함 축). over 키(셀이 유래한 반복영역) → ctx.dataDefs에서 binding 필드의 widget을 조회해
 * photo면 <img> 렌더용 URL을 돌려준다 — choiceGroup의 dataDef options 파생과 같은 열쇠.
 *
 * 반복 밖/다른 widget/빈 값·비문자열 값은 undefined → 종전 경로 그대로(회귀 0). 저작자가 칼럼
 * 매핑(셀 지정)으로 명시 image 노드를 받은 셀(content=[image])은 content 경로가 binding보다
 * 먼저라 여기 도달하지 않는다(이중 렌더 없음).
 *
 * url은 formatted(셀 binding.format 적용 후) 우선 — image 노드가 format(url host 치환)을 안쪽
 * url에 적용하는 것과 정렬. 단 게이트는 raw 기준이라 empty/fallback('-') format이 빈 값을
 * 사진으로 둔갑시키지 않는다.
 */
/**
 * 반복영역 서명 슬롯 칼럼(dataSource widget:'signature') binding 셀 렌더(P2).
 *
 * photoCellUrl과 같은 결의 특례이되 축이 다르다 — photo는 dataDefs(내장 데이터셋) 칼럼,
 * 서명 슬롯은 dataSources(DataSourceFieldMap) 칼럼이고, 값도 content 행 값이 아니라
 * resolved 채널(scope의 `__slotSignatures` — resolveForRender가 liveData에서 통과)에서 읽는다
 * (서명은 content에 살지 않는다, 설계 §3-2). 서명이 없으면 빈 서명 칸을 그린다 —
 * 텍스트 폴백으로 흐르면 placeholder/빈칸이 되어 "채워질 서명 자리"가 사라진다.
 *
 * scope는 반복 행 머지 스코프(`{...top, ...row}`)라 rowId(행 정체성)·signerNameField(이름 칼럼)·
 * `__slotSignatures`(top-level 통과분)를 전부 여기서 읽을 수 있다. 반복 밖/다른 widget이면
 * undefined → 종전 경로 그대로(회귀 0).
 */
function signatureSlotCellNode(
  cell: TableCell,
  ctx: RenderContext,
  overKey: string | undefined,
  scope: RenderData,
): ReactNode | undefined {
  if (overKey === undefined || !cell.binding) return undefined;
  const field = ctx.dataSources
    ?.find((d) => d.key === overKey)
    ?.fields.find((f) => f.field === cell.binding?.bindingKey);
  if (!field || field.widget !== 'signature') return undefined;
  const row = scope as Record<string, unknown>;
  const { view } = resolveSlotSignatureDisplay(
    scope[SLOT_SIGNATURES_CONTENT_KEY],
    overKey,
    field,
    row,
  );
  return (
    <SignatureSlotView value={view?.signatureData} signerName={rowSignerName(row, field)} />
  );
}

function photoCellUrl(
  cell: TableCell,
  raw: unknown,
  formatted: unknown,
  ctx: RenderContext,
  overKey: string | undefined,
): string | undefined {
  if (overKey === undefined || !cell.binding) return undefined;
  if (typeof raw !== 'string' || raw.trim() === '') return undefined;
  const bindingKey = cell.binding.bindingKey;
  const field = ctx.dataDefs
    ?.find((d) => d.key === overKey)
    ?.fields.find((f) => f.key === bindingKey);
  if (field?.widget !== 'photo') return undefined;
  const url = typeof formatted === 'string' ? formatted.trim() : raw.trim();
  return url === '' ? undefined : url;
}

/**
 * 셀 1개 — content(노드 재귀) > text > aggregate > compute > binding 값. th/td + 병합/정렬/스타일.
 * rowSpan/colSpan은 grid 배치 시 clamp된 값을 받아 그리드 일관성을 유지한다(span=1이면 속성 생략).
 */
function renderTableCell(
  cell: TableCell,
  key: string,
  scope: RenderData,
  ctx: RenderContext,
  rowSpan: number,
  colSpan: number,
  tableId: string,
  cellRow: number,
  cellCol: number,
  /** 페이지 분할 continuation 병합 라벨에 붙일 접미어(예 ' (계속)'). 문자열 콘텐츠에만 적용. */
  continuationSuffix?: string,
  /**
   * keep-together(반복영역 groupStartOnNewPage) 대상 묶음 그룹 셀의 **클램프 이전 원본** rowspan.
   * 이 셀에만 `data-composer-rowspan-full`로 방출해 measureTable이 그 그룹만 안전 경계로 삼게 한다
   * (나머지 병합/그룹은 자유 컷). 클램프된 rowSpan으로 측정하면 관통 경계를 "안전"으로 오판하므로 원본을
   * 쓴다(clip↔measure 피드백 루프 방지). 비keep/continuation 셀은 undefined. span<=1이면 미방출.
   */
  fullRowSpan?: number,
  /** 순번 prefix(region.indexPrefix) — 문자열 표시값/placeholder 앞에 그대로 prepend(셀 format과 독립). */
  indexPrefix?: string,
  /** 이 셀이 유래한 반복영역의 over 키(반복 셀만) — 셀 content choiceGroup의 dataDef options 파생용. */
  overKey?: string,
): ReactNode {
  const style: CSSProperties = {};
  // 셀 텍스트의 줄바꿈(\n, Shift+Enter)·연속 공백을 보존하고 박스 너비에서 자동 줄바꿈한다.
  // td 기본 white-space:normal은 \n을 공백으로 collapse해 편집기 줄바꿈이 렌더에서 사라진다.
  // 줄바꿈 없는 셀은 동일하게 한 줄로 보이고, 노드 콘텐츠(cell.content) 경로에도 무해하다.
  style.whiteSpace = 'pre-wrap';
  const cH = cell.contentAlignH;
  if (cH) style.textAlign = contentHToTextAlign(cH);
  if (cell.contentAlignV) style.verticalAlign = vAlignToTableCss(cell.contentAlignV);
  // 공통 셀 스타일(폰트/배경/테두리 변별 합성/패딩)은 cellStyleToCss로 일원화(formTable 고정 셀과 공유).
  Object.assign(style, cellStyleToCss(cell.style));
  // 배경 데이터 바인딩(backgroundKey) — resolve된 scope에서 색 문자열을 읽어 셀 배경으로.
  // 값이 있고 색 형식이면(검증됨) 정적 background보다 **우선** 적용, 없으면 정적 배경 유지
  // (편집 placeholder 모드처럼 값이 아직 없을 땐 자연히 정적 fallback). 도메인 무관 범용 도구.
  if (cell.backgroundKey) {
    const bg = resolveCellBackgroundColor(scope, cell.backgroundKey, cell.backgroundKeyAlpha);
    if (bg !== undefined) style.background = bg;
  }

  let content: ReactNode;
  // 바인딩 {label} placeholder 텍스트(있으면) — 줄바꿈 없이 폰트 축소(FitPlaceholder)로 렌더.
  let placeholderText: string | null = null;
  // rangeMap 배지(알약) 셀이면 그 노드 — 순번 prefix/`(계속)` 접미어를 **알약 바깥에** 붙이려고
  // 따로 들고 있는다. 문자열 content만 처리하던 종전 로직은 배지 셀에서 그 둘을 조용히 잃었다
  // (순번 칼럼·페이지 분할 표에 배지를 걸면 번호가 사라진다). 배지가 아니면 null.
  let badgeNode: ReactNode | null = null;
  // 값 출처 표식(모사/연결 보고서) — **실값 텍스트가 그려지는 갈래**에서만 채운다. 셀 style에
  // 점선 밑줄을 병합하고 td에 title을 건다(레이아웃 중립 — 셀 크기·페이지 분할 측정 불변).
  let provenanceTitle: string | undefined;
  // aggregate/compute/binding/label 4경로 공통: placeholder 문자열을 content·회색으로 대입.
  // placeholderText는 반환값을 호출부에서 직접 대입 — 클로저 내부 대입은 TS 흐름 분석에
  // 안 보여 이후 사용처에서 never로 좁혀지는 문제(TS2322)를 피한다.
  const setPlaceholder = (p: string): string => {
    content = p;
    // 'unresolvable-only' 모드는 amber(그 외 기존 회색) — 이 모드에서 뜨는 placeholder는 전부
    // 미해소(live/보고서-스코프) 판정을 통과한 것이라 ctx만으로 톤이 정해진다.
    style.color = bindingPlaceholderColor(ctx);
    return p;
  };
  // 값소스 우선순위(content > 비어있지 않은 text > aggregate > compute > binding)는
  // effectiveCellSourceKind로 일원화한다. content/정적 text는 값 소스가 아니므로(각자 아래에서
  // 직접 렌더) 여기서는 그 두 분기를 먼저 처리하고, 나머지 데이터 값 소스(aggregate/compute/binding)를
  // sourceKind로 분기한다 — 판정 로직이 admin 셀 배지(collectBoundCells)와 정확히 일치한다.
  const sourceKind = effectiveCellSourceKind(cell);
  if (cell.content) {
    // 반복영역 유래 셀이면 over 키를 per-cell ctx에 실어 content의 choiceGroup이 dataDef options를
    // 파생하게 한다(clone이라 형제 셀로 누출 없음). over가 없으면 원본 ctx 그대로(회귀 0).
    const contentCtx = overKey !== undefined ? { ...ctx, cellOverKey: overKey } : ctx;
    content = cell.content.map((n) => renderNode(n, scope, contentCtx));
  } else if (
    cell.text !== undefined &&
    !(cell.text === '' && (cell.binding || cell.compute || cell.aggregate))
  ) {
    // text는 "비어있지 않을 때만" 우선한다. 빈 문자열 `text:''`이면서 값 소스(binding·compute·
    // aggregate)가 공존하면(빈 셀은 templateOps가 text:''로 생성하므로 그 위에 값 소스를 건 경우)
    // text를 값 없음으로 보고 아래 값 소스 경로로 fallback해 placeholder/실값/계산·집계 결과가
    // 가려지지 않게 한다. 정적 빈 텍스트 셀(값 소스 없음)은 종전대로 빈칸 유지.
    content = cell.text;
  } else if (sourceKind === 'aggregate' && cell.aggregate) {
    // 바인딩 보기에서 집계 대상 배열(over)이 미적용이면 가짜 0 대신 {개수}/{합계}류 placeholder.
    const aggPlaceholder = aggregatePlaceholderText(
      cell.aggregate,
      ctx,
      getByPath(scope, cell.aggregate.over),
      scope,
    );
    if (aggPlaceholder !== undefined) {
      placeholderText = setPlaceholder(aggPlaceholder);
    } else {
      // 산출 수치와 표시 문자열을 나눠 얻는다 — rangeMap 배지가 같은 수치로 구간을 판정하게.
      const aggValue = computeCellAggregateValue(cell.aggregate, scope);
      const aggText = String(applyFormat(aggValue, cell.aggregate.format, scope) ?? '');
      const agg = rangeCellContent(aggValue, cell.aggregate.format, scope, aggText);
      content = agg.content;
      badgeNode = agg.badgeNode;
    }
  } else if (sourceKind === 'compute' && cell.compute) {
    // 바인딩 보기에서 compute의 바인딩 입력이 모두 미해석이면 가짜 0 대신 {합계}류 placeholder.
    const computePlaceholder = computePlaceholderText(
      cell.compute,
      ctx,
      (key) => readBindingValue(scope, key),
      scope,
    );
    if (computePlaceholder !== undefined) {
      placeholderText = setPlaceholder(computePlaceholder);
    } else {
      // 계산 셀 — named-input/상수를 op/안전수식으로 조합(빈값이면 아래 빈 셀 가드가 nbsp 처리).
      // 수치와 표시 문자열을 나눠 얻는다 — rangeMap 배지가 같은 수치로 구간을 판정하게
      // (위험성평가 매트릭스 점수 셀이 계산+구간 판정 조합이라 배지 축이 여기에도 걸린다).
      const computeValue = computeCellComputeValue(cell.compute, scope);
      const computeText =
        computeValue === '' ? '' : String(applyFormat(computeValue, cell.compute.format, scope) ?? '');
      const computed = rangeCellContent(computeValue, cell.compute.format, scope, computeText);
      content = computed.content;
      badgeNode = computed.badgeNode;
    }
  } else if (sourceKind === 'binding' && cell.binding) {
    // 표 셀은 "이미 resolve된" RenderData(scope)에서 bindingKey로 읽는다.
    // (resolver가 작성 시 api.select를 적용해 bindingKey로 저장 + repeat 항목 scope는
    //  transform 배열의 항목 필드명=bindingKey이므로, select가 아닌 bindingKey 조회가 옳다.)
    // 셀 binding.format이 있으면 표시용 포맷(unit 등)을 적용한다(render 후처리, 비표 노드와 동일).
    const raw = readBindingRaw(scope, cell.binding);
    const formatted = applyFormat(raw, cell.binding.format, scope);
    // 미적용 카탈로그 바인딩이면 {label} placeholder(회색). 게이트는 **raw 기준** — format의
    // empty/fallback('-')이 적용된 값으로 판정하면 바인딩 보기에서 placeholder가 안 뜬다
    // (정책: 바인딩 보기=바인딩 정보 우선, fallback은 실데이터/실렌더에서만. 비표 노드와 동일).
    const placeholder = bindingPlaceholderText(cell.binding, raw, ctx, cell.binding.format, scope);
    // 편집 캔버스에서 repeat 패턴 셀(데이터 0이라 빈값)이면 {필드라벨}로 매핑을 표시(편집 보조).
    const labelPlaceholder =
      placeholder === undefined
        ? bindingLabelPlaceholderText(cell.binding, raw, ctx, cell.binding.format, scope)
        : undefined;
    // photo 칼럼(dataDef widget:'photo') 반복 셀 — URL 원문을 텍스트로 흘리지 않고 <img>로.
    const photoUrl = photoCellUrl(cell, raw, formatted, ctx, overKey);
    // 서명 슬롯 칼럼(dataSource widget:'signature') 반복 셀 — placeholder류보다 먼저 라우팅한다:
    // 빈 서명 칸(밑줄 상자)이 곧 이 셀의 정상 표시라, 캔버스({필드라벨})·바인딩 보기에서도
    // 같은 모양이어야 실폼과 갈라지지 않는다(WYSIWYG).
    const signatureNode = signatureSlotCellNode(cell, ctx, overKey, scope);
    if (signatureNode !== undefined) {
      content = signatureNode;
    } else if (placeholder !== undefined) {
      placeholderText = setPlaceholder(placeholder);
    } else if (labelPlaceholder !== undefined) {
      placeholderText = setPlaceholder(labelPlaceholder);
    } else if (photoUrl !== undefined) {
      // 칼럼 매핑 특례가 만드는 image 노드 기본(templateOps createNode: width:'fill'·minHeight 80·
      // fit:'contain')과 같은 결 — 같은 photo 칼럼이 셀 지정 방식(명시 image 노드 vs binding 셀)에
      // 따라 다른 크기가 되지 않게 한다. 로드 후 자연 높이 변화는 PagedComposerView의
      // ResizeObserver 재측정이 흡수(페이지 분할 실측과 무충돌). 시각값은 인라인 소유(rc Tailwind 금지 규약).
      // 로드 실패는 같은 발자국(minHeight 80)의 안내 칸으로 접는다(ImageWithFallback 헤더 — P6-d).
      content = (
        <ImageWithFallback
          src={photoUrl}
          alt=""
          style={{ width: '100%', minHeight: 80, objectFit: 'contain', display: 'block' }}
        />
      );
    } else if (cell.binding.format?.kind === 'bool' && cell.binding.format.display === 'box') {
      // bool box format — 문자(V) 대신 체크박스 비주얼(checkbox 노드와 동일 헬퍼). 체크 판정=isFlagTruthy.
      content = <CheckBox checked={isFlagTruthy(raw)} boxSize={cell.binding.format.boxSize ?? 13} />;
    } else {
      // rangeMap 표시 — 같은 표시 문자열을 알약으로 감싸고 병기 값을 옆에 붙인다
      // (배지 미매칭이면 종전 문자열, 병기가 있으면 그 뒤에 나란히).
      //
      // ⚠️ 기간(date-range) 값은 **객체**라 String()을 그대로 타면 '[object Object]'가 셀에 찍힌다
      // (annotated-image가 같은 성질의 선례 — groupCellValue도 같은 이유로 객체를 갈라 둔다).
      // 형식(format)이 지정돼 있으면 applyFormat(kind:'date')이 이미 문자열로 접었으므로 여기 값은
      // 객체가 아니고, 형식 **미지정**일 때만 이 분기가 산다. 그 기본 표기는 단일 date 셀과 패리티로
      // raw끼리 잇는다("2026-08-28 ~ 2026-08-30") — 한글 표기는 형식 지정과 작성 페이지 몫이다.
      const text = isDateRangeValue(formatted) ? dateRangeRawText(formatted) : String(formatted ?? '');
      const ranged = rangeCellContent(raw, cell.binding.format, scope, text);
      content = ranged.content;
      badgeNode = ranged.badgeNode;
      // 이 갈래(그리고 이 갈래만)가 바인딩 실값을 **텍스트로** 그린다 — 사진/체크박스/서명 칸은
      // 밑줄을 걸 글자가 없어 대상이 아니다(placeholder 갈래는 위에서 이미 갈라져 나갔다).
      const provenance = bindingProvenanceMark(cell.binding, raw, ctx);
      if (provenance) {
        Object.assign(style, provenance.style);
        provenanceTitle = provenance.title;
      }
    }
  } else {
    content = '';
  }

  // 페이지 분할 continuation 병합 라벨의 ` (계속)` 접미어 + 순번 prefix(region.indexPrefix).
  // 셀 format과 독립인 표시 장식이라 어느 값 소스에도 같이 붙는다:
  //  - placeholder 텍스트(편집 힌트)에도 붙여 어느 렌더 경로든 표식·순번이 보이게 한다.
  //  - **rangeMap 배지 셀은 알약 바깥에** 붙인다 — 알약 안에 순번이 들어가면 배지가 값이 아니라
  //    문장이 된다. 종전에는 `typeof content === 'string'` 가드에 걸려 배지 셀이 순번/`(계속)`을
  //    조용히 잃었다(순번 칼럼·페이지 분할 표에 badge를 걸면 번호가 사라진다).
  //  - 노드 콘텐츠(cell.content)·bool box 체크박스는 대상 아님(종전 그대로).
  if (placeholderText !== null) {
    // placeholderText !== null이면 return이 FitPlaceholder(placeholderText)를 쓰므로 content 대입은 dead.
    if (continuationSuffix && placeholderText !== '') placeholderText += continuationSuffix;
    if (indexPrefix) placeholderText = indexPrefix + placeholderText;
  } else if (badgeNode !== null) {
    if (indexPrefix || continuationSuffix) {
      content = (
        <>
          {indexPrefix}
          {badgeNode}
          {continuationSuffix}
        </>
      );
    }
  } else {
    if (continuationSuffix && typeof content === 'string' && content !== '') {
      content += continuationSuffix;
    }
    if (indexPrefix && typeof content === 'string') content = indexPrefix + content;
  }

  // 빈 셀도 최소 한 줄 높이 확보(auto 행에서 0 높이로 줄어 편집/표 틀이 깨지는 것 방지).
  // content가 빈 문자열/누락이면 nbsp()로 한 줄 line-box를 만든다. 일반 공백은 td에서
  // whitespace collapse로 사라져 line-box를 못 만드니 반드시 non-breaking space를 쓴다.
  // 노드 콘텐츠(cell.content) 경로는 ReactNode[]라 === '' 가 false → 영향 없음.
  if (content === '' || content == null) content = '\u00A0';

  const common = {
    // span=1이면 속성 생략(불필요한 colSpan/rowSpan=1 방지, 기존 동작 보존).
    colSpan: colSpan > 1 ? colSpan : undefined,
    rowSpan: rowSpan > 1 ? rowSpan : undefined,
    style,
    // 셀 선택 역추적용 좌표 attribute(`tableId::row::col`). data-composer-node-id처럼 항상 방출
    // (web/PDF 무해 — 편집기가 closest()로 읽고, 렌더 자체엔 영향 없음).
    'data-composer-cell': `${tableId}::${cellRow}::${cellCol}`,
    // keep-together 묶음 그룹 셀의 원본 rowspan(클램프 이전) — measure가 이 그룹만 안전 경계로 삼는다.
    // 비keep 셀/continuation 셀엔 미방출(fullRowSpan undefined). 클램프된 rowSpan 오염 방지(clip↔measure).
    'data-composer-rowspan-full': fullRowSpan && fullRowSpan > 1 ? fullRowSpan : undefined,
  };
  // 'unresolvable-only' 모드 placeholder는 안내 툴팁을 라벨과 함께(그 외 undefined = 기존 라벨 title).
  const phTitle = bindingPlaceholderTitle(ctx);
  // 셀 대각선(사선) 오버레이 — 절대배치 SVG라 셀 크기·행 높이·페이지 분할 측정에 중립.
  // style.diagonal 미지정이면 null(회귀 0). 기준 박스(position:relative)는 cellStyleToCss가 부여.
  const diagonal = cellDiagonalOverlay(cell.style);
  const inner =
    placeholderText !== null ? (
      <FitPlaceholder
        text={placeholderText}
        fill
        title={phTitle ? `${placeholderText} — ${phTitle}` : undefined}
      />
    ) : (
      content
    );
  return (
    <td key={key} {...common} title={provenanceTitle}>
      {/* ⚠️ 대각선이 없는 셀은 **children 형상을 바꾸지 않는다** — 오버레이 슬롯을 무조건 두면
          `{null}{content}`가 되어 td children이 배열(`[null, 'C']`)로 바뀐다. 표 렌더 계약을 읽는
          기존 spec(`__tests__/table.test.ts`의 `children).toBe('C')`)이 그 형상에 의존하므로
          「대각선 없는 셀은 무영향」이 성립해야 한다(회귀 0). 대각선이 있는 셀만 Fragment로 감싼다
          — Fragment는 DOM에 나타나지 않아 오버레이 렌더 결과는 동일하다.
          못 박는 곳: `__tests__/cellDiagonal.test.tsx` 「대각선 없는 셀은 children 형상 불변」. */}
      {diagonal === null ? (
        inner
      ) : (
        <>
          {diagonal}
          {inner}
        </>
      )}
    </td>
  );
}

/**
 * 템플릿 전체 렌더 — A4 page 컨테이너 + body를 column flow로 배치.
 * specialComponents: special 노드 렌더용 외부 주입(web/admin). 미지정 시 placeholder.
 */
export function RenderTemplate({
  template,
  data,
  specialComponents,
  bindingPlaceholders,
  catalog,
  bindingProvenance,
  editing,
  bindingLabels,
  rowRanges,
  itemRanges,
  leadSuppressed,
  chunkRanges,
  fillHeights,
  fillSlacks,
  activeBranchSlots,
}: {
  template: ReportTemplate;
  data: RenderData;
  specialComponents?: RenderContext['specialComponents'];
  /**
   * admin 편집/미리보기 전용 — 미적용(빈값) 카탈로그 바인딩에 {label} placeholder 표시(회귀 0).
   * boolean 종전 의미 불변. 'unresolvable-only'는 저작 중 원리적 부재(live/보고서-스코프)만
   * amber 톤으로 표시(실데이터 반영 중 모드 — types.ts BindingPlaceholdersMode 참조).
   */
  bindingPlaceholders?: RenderContext['bindingPlaceholders'];
  /** placeholder label lookup용 카탈로그 인덱스(bindingPlaceholders=true일 때 함께 전달). */
  catalog?: RenderContext['catalog'];
  /**
   * admin 저작 미리보기 전용 — 채워진 값의 출처(모사 값/연결 보고서 저장값) 표시 재료.
   * 미지정=표시 없음(web 실렌더와 동일, 회귀 0). 형상은 types.ts BindingProvenanceContext 참조.
   */
  bindingProvenance?: RenderContext['bindingProvenance'];
  /** admin 편집 캔버스 전용 — 표 반복영역/다단을 전개하지 않고 원본 패턴 행을 렌더(미리보기/출력은 전개). */
  editing?: boolean;
  /** admin 편집 캔버스 전용 — repeat 패턴 셀 bindingKey→필드 라벨 맵({필드라벨} placeholder용). */
  bindingLabels?: RenderContext['bindingLabels'];
  /** admin 편집 캔버스 페이지 분할 전용 — 표 nodeId→[startRow,endRow) 행 범위만 렌더(회귀 0). */
  rowRanges?: RenderContext['rowRanges'];
  /** admin 편집 캔버스 페이지 분할 전용 — repeat nodeId→[startItem,endItem) 항목 범위만 렌더(회귀 0). */
  itemRanges?: RenderContext['itemRanges'];
  /** 중첩 반복 분할 전용 — 조각 continuation 항목 경로 표식(그 낱장에선 lead 미렌더, 회귀 0). */
  leadSuppressed?: RenderContext['leadSuppressed'];
  /** 순차 다경계 분할 전용 — 항목 경로→흐름 청크 창(그 낱장에선 창의 청크만 렌더, 회귀 0). */
  chunkRanges?: RenderContext['chunkRanges'];
  /** admin 편집 캔버스 페이지 채움 전용 — group/stack nodeId→채움 높이(px)(회귀 0). */
  fillHeights?: RenderContext['fillHeights'];
  /** admin 편집 캔버스 페이지 채움 전용(표) — pageFill 표 nodeId→잔여 slack(px), 빈 괘선 행 페인팅(회귀 0). */
  fillSlacks?: RenderContext['fillSlacks'];
  /** admin 편집 캔버스 전용 — branch nodeId→활성 슬롯 인덱스(editing일 때 그 슬롯만 렌더, 회귀 0). */
  activeBranchSlots?: RenderContext['activeBranchSlots'];
}) {
  const ctx: RenderContext = {
    specialComponents,
    bindingPlaceholders,
    catalog,
    bindingProvenance,
    rowRanges,
    itemRanges,
    leadSuppressed,
    chunkRanges,
    fillHeights,
    fillSlacks,
    editing,
    bindingLabels,
    activeBranchSlots,
    // 선택지 그룹(choiceGroup) options 파생용 — 템플릿 내장 데이터셋 정의를 렌더 컨텍스트로 전달.
    dataDefs: template.dataDefs,
    // 서명 슬롯 셀 판정용(P2) — 반복영역 binding 셀이 over 키로 widget:'signature'를 조회한다.
    dataSources: template.dataSources,
  };
  const { margins } = template.page;
  const pageStyle: CSSProperties = {
    width: `${pagePx(template.page).w}px`,
    paddingTop: `${margins.top}px`,
    paddingRight: `${margins.right}px`,
    paddingBottom: `${margins.bottom}px`,
    paddingLeft: `${margins.left}px`,
    boxSizing: 'border-box',
    display: 'flex',
    flexDirection: 'column',
    backgroundColor: '#ffffff',
    // 기본 폰트 본명조(Noto Serif KR). 노드/셀이 fontFamily를 명시하면 그 값이 우선 override된다
    // (각 노드/셀 렌더가 자신의 fontFamily를 직접 style에 설정하므로 상속보다 강함).
    fontFamily: "'Noto Serif KR', serif",
  };
  return (
    <div style={pageStyle}>
      {template.body.map((node) => renderNode(node, data, ctx))}
    </div>
  );
}
